/* Справка по функциям системы.
 *
 * До этого «Справка и поддержка» была надписью в подвале меню: она ничего не
 * открывала, и прочитать, что делает раздел и по каким правилам он работает,
 * было негде. Спрашивали одно и то же — почему номер не принимается, откуда
 * берётся объект строки, кто выпускает возвратный трансмиттал, — и ответ жил
 * в голове внедренца.
 *
 * Справка — ДАННЫЕ, а не разметка: статьи лежат массивом, вид у них один.
 * Добавить раздел значит дописать статью, а не собрать ещё одну страницу.
 *
 * Каждая статья описывает ЭКРАН: что он делает, какие на нём вкладки, какие
 * кнопки и что происходит по нажатию. Названия кнопок в тексте — те же слова,
 * что на экране: справка, называющая кнопку иначе, отправляет человека искать
 * несуществующее.
 *
 * Правило содержания: здесь описано только то, что в системе есть. Справка,
 * обещающая несуществующее, хуже её отсутствия — по ней принимают решения.
 */
import { useMemo, useState } from 'react'

import { Icon } from '@/ds/icons'
import { Drawer, EmptyState, Input } from '@/ui'
import { t } from '@/i18n/lang'
import type { Lang } from '@/i18n/lang'

export interface HelpArticle {
  id: string
  icon: string
  title: { ru: string; en: string; zh: string }
  /* Тело — строки. Начало строки задаёт вид: «## » подзаголовок, «• » пункт
     списка, остальное — абзац. Разметки богаче справка не требует, а разбор
     в три ветки читается лучше дерева блоков. */
  body: { ru: string[]; en: string[]; zh: string[] }
}

export const HELP: HelpArticle[] = [
  {
    id: 'start',
    icon: 'compass',
    title: { ru: 'С чего начать', en: 'Getting started', zh: '从这里开始' },
    body: {
      ru: [
        'Система ведёт документацию проекта: реестры MDR и VDR, подачи трансмитталами, рассмотрение с замечаниями и сдачу комплектов.',
        '## Что видно именно вам',
        'Разделы меню показываются по правам роли. Пункта нет — права на него нет: администратор выдаёт их на вкладке «Роли и доступ», и сервер отказывает по той же матрице, а не по списку в коде.',
        'Подрядчик видит только свои документы, трансмитталы и замечания. Это не настройка экрана: выборка ограничена на сервере.',
        '## Верхняя строка',
        '• название проекта слева — переключение проекта, если их у вас несколько;',
        '• «Поиск по № документа, TRM, CRS…» (Ctrl + K) — быстрый переход по номеру;',
        '• RU / EN / ZH — язык интерфейса. Данные остаются на языке источника;',
        '• значок луны или солнца — тёмная и светлая тема;',
        '• значок панели — свернуть меню в полосу значков и развернуть обратно;',
        '• кружок с инициалами — учётная запись, смена проекта и выход.',
        '## Любая таблица',
        '• заголовок столбца — сортировка; значок воронки в заголовке — отбор значений; «Сброс» и «Применить» внутри него;',
        '• столбец переносится перетаскиванием заголовка, ширина тянется за его границу;',
        '• «Колонки» — какие столбцы показывать; «Вернуть состав, ширины и порядок столбцов» — сброс раскладки;',
        '• раскладка запоминается в браузере на каждую таблицу отдельно.',
        '## Порядок работы',
        '• заказчик заводит реестр MDR на контракт;',
        '• подрядчик наполняет состав — вручную или файлом — и подаёт на согласование;',
        '• документы подаются трансмитталом, проходят рассмотрение и получают код;',
        '• замечания ведутся листами CRS, ответы — ACSR;',
        '• принятые документы собираются в комплекты сдачи.',
      ],
      en: [
        'The system runs the project documentation: MDR and VDR registers, submissions by transmittal, review with comments, and handover packages.',
        '## What you can see',
        'Menu sections follow your role permissions. No item means no permission: an administrator grants it on the «Roles» tab, and the server refuses by the same matrix rather than by a list in the code.',
        'A contractor sees only its own documents, transmittals and comments. That is not a screen setting: the query is limited on the server.',
        '## The top bar',
        '• the project name on the left — switches the project when you have several;',
        '• «Search by document, TRM, CRS number…» (Ctrl + K) — jump by number;',
        '• RU / EN / ZH — interface language. The data stays in its source language;',
        '• the moon or sun icon — dark and light theme;',
        '• the panel icon — collapses the menu to a rail of icons and back;',
        '• the circle with your initials — account, project switch and sign out.',
        '## Any table',
        '• the column header sorts; the funnel icon in the header filters values, with «Reset» and «Apply» inside;',
        '• a column is moved by dragging its header, the width is dragged by its edge;',
        '• «Columns» — which columns to show; «Restore columns, widths and order» — resets the layout;',
        '• the layout is remembered in the browser per table.',
        '## The flow',
        '• the owner creates an MDR register for a contract;',
        '• the contractor fills the content — by hand or from a file — and submits it for approval;',
        '• documents are submitted by transmittal, reviewed and given a code;',
        '• comments are kept on CRS sheets, replies as ACSR;',
        '• accepted documents are collected into handover packages.',
      ],
      zh: [
        '本系统管理项目文件：MDR 与 VDR 登记册、以传递单提交、带意见的审查、以及竣工资料的移交。',
        '## 您能看到什么',
        '菜单按角色权限显示。没有该菜单即没有该权限：管理员在「角色与权限」页签中授权，服务器也按同一张权限表拒绝，而不是按代码里的清单。',
        '承包商只能看到自己的文件、传递单和意见。这不是界面设置：查询在服务器端已受限。',
        '## 顶部栏',
        '• 左侧的项目名称——当您有多个项目时用于切换；',
        '• 「按文件号、TRM、CRS 检索…」（Ctrl + K）——按编号快速跳转；',
        '• RU / EN / ZH——界面语言。数据仍保持来源语言；',
        '• 月亮或太阳图标——深色与浅色主题；',
        '• 面板图标——把菜单收起为图标条，或重新展开；',
        '• 带姓名缩写的圆形——账户、切换项目与退出。',
        '## 所有表格通用',
        '• 点击列标题排序；标题中的漏斗图标用于按值筛选，其中含「重置」和「应用」；',
        '• 拖动列标题可移动该列，拖动其边框可调整宽度；',
        '• 「列」——选择显示哪些列；「恢复列的组成、宽度和顺序」——重置布局；',
        '• 布局按表分别记忆在浏览器中。',
        '## 工作流程',
        '• 业主按合同建立 MDR 登记册；',
        '• 承包商填写内容——手工录入或用文件导入——并提交审批；',
        '• 文件以传递单提交，经审查并获得审查代码；',
        '• 意见以 CRS 清单管理，回复为 ACSR；',
        '• 已批准的文件汇集成移交资料套。',
      ],
    },
  },
  {
    id: 'inbox',
    icon: 'inbox',
    title: { ru: 'Мои действия', en: 'My actions', zh: '我的待办' },
    body: {
      ru: [
        'Личная очередь: то, что ждёт именно вашего решения — приёмка трансмиттала, шаг рассмотрения, решение по замечанию, выпуск возвратного TRM.',
        '## Кнопки на экране',
        '• «По действию», «По сроку», «По дисциплине» — как сгруппировать очередь;',
        '• плитки «Всего задач», «Просрочено», «Срок ≤ 2 дней» — быстрый отбор по срочности;',
        '• «Принять» в строке — открывает входной контроль трансмиттала;',
        '• «Рассмотреть» в строке — открывает шаг рассмотрения на документе.',
        'Просроченное считается по сроку ШАГА, а не по сроку документа: шаг просрочен и тогда, когда до срока документа ещё далеко.',
        'Кнопка в строке ведёт туда, где действие выполняется: очередь не дублирует экраны, она их открывает.',
      ],
      en: [
        'Your personal queue: what waits for your decision — accepting a transmittal, a review step, a comment decision, issuing the return TRM.',
        '## Buttons on the screen',
        '• «By action», «By due date», «By discipline» — how the queue is grouped;',
        '• the «Total», «Overdue», «Due ≤ 2 days» tiles — a quick filter by urgency;',
        '• «Accept» in a row — opens the intake control of a transmittal;',
        '• «Review» in a row — opens the review step on the document.',
        'Overdue is counted by the STEP due date, not the document date: a step can be overdue while the document date is still far away.',
        'The button in a row opens the screen where the action happens: the queue does not duplicate screens, it navigates to them.',
      ],
      zh: [
        '个人队列：等待您决定的事项——接收传递单、审查步骤、意见处理、签发回传传递单。',
        '## 界面上的按钮',
        '• 「按动作」「按期限」「按专业」——队列的分组方式；',
        '• 「任务总数」「已逾期」「期限 ≤ 2 天」卡片——按紧急程度快速筛选；',
        '• 行内「接收」——打开传递单的入库检验；',
        '• 行内「审查」——在文件上打开审查步骤。',
        '逾期按「步骤」期限计算，而非文件期限：文件期限尚远时，步骤也可能已逾期。',
        '行内按钮直接跳转到执行该动作的界面：队列不重复界面，只打开它们。',
      ],
    },
  },
  {
    id: 'dashboard',
    icon: 'layout-dashboard',
    title: { ru: 'Обзор проекта', en: 'Project overview', zh: '项目概览' },
    body: {
      ru: [
        'Сводка по проекту: сколько документов выпущено, что идёт с отставанием, как загружены стороны.',
        '## Что на экране',
        '• «S-кривая выпуска документации» — накопленный выпуск: план, прогноз, факт;',
        '• «Комплектность по реестрам MDR» — доля принятых документов по каждому реестру;',
        '• «Поток документов (вход / возврат)» — сколько пришло и сколько ушло обратно за период;',
        '• «Сроки под контролем» — что просрочено и что подходит к сроку;',
        '• «Лента активности» — последние события проекта;',
        '• «Все реестры» — переход к списку реестров MDR.',
        'Цифры считает сервер по текущим данным: экран ничего не хранит и не досчитывает от себя.',
      ],
      en: [
        'A project summary: how many documents are issued, what is behind schedule, how loaded each side is.',
        '## What is on the screen',
        '• «Document issue S-curve» — cumulative issue: plan, forecast, actual;',
        '• «MDR completeness» — the share of accepted documents per register;',
        '• «Document flow (in / returned)» — how much arrived and how much went back over the period;',
        '• «Dates under control» — what is overdue and what is coming due;',
        '• «Activity feed» — the latest project events;',
        '• «All registers» — opens the list of MDR registers.',
        'The numbers are computed by the server from the current data: the screen stores nothing and adds nothing of its own.',
      ],
      zh: [
        '项目总览：已签发多少文件、哪些进度滞后、各方负荷如何。',
        '## 界面内容',
        '• 「文件签发 S 曲线」——累计签发：计划、预测、实际；',
        '• 「各 MDR 登记册齐套度」——每个登记册中已批准文件的比例；',
        '• 「文件流（接收 / 退回）」——本期收到多少、退回多少；',
        '• 「期限监控」——哪些已逾期、哪些临近期限；',
        '• 「动态记录」——项目最近的事件；',
        '• 「全部登记册」——跳转到 MDR 登记册列表。',
        '数字由服务器按当前数据计算：界面不保存也不自行补算。',
      ],
    },
  },
  {
    id: 'mdr',
    icon: 'folder-tree',
    title: { ru: 'Реестр MDR', en: 'MDR register', zh: 'MDR 登记册' },
    body: {
      ru: [
        'Реестр документации по контракту. Реестр на контракт один: контракт — его деловой ключ, подрядчик и сроки наследуются от контракта. Слева список реестров, справа состав выбранного.',
        '## Кнопки над списком реестров',
        '• «Создать MDR» — новый реестр: контракт, стадия, наименование, маска номера документа. Строка «без контракта» заводит реестр собственной документации заказчика — там же указывается объект, он входит в его код;',
        '• «Карточка MDR» — карточка выбранного реестра: контракт, подрядчик, стадия, объект, маска с образцом номера, состояние, согласование и состав.',
        '## Кнопки над составом',
        '• «Создать документ» — завести строку вручную: наименование, дисциплина, тип, назначение, титул, объект и сроки по стадиям — план и прогноз у каждой (IFR, IFA, AFD). Отдельного общего плана нет: им становится план той стадии, в которой документ сейчас. Порядок стадий и попадание в срок контракта проверяются сразу — как у присланного файла;',
        '• «Импорт реестра» — загрузить состав файлом;',
        '• «Проверить файл» — прогнать файл по тем же правилам, ничего не создавая: сухой прогон с отчётом;',
        '• «Исторический реестр» — режим переноса идущего проекта: прошедшие сроки, состояние и код принимаются как есть;',
        '• «Файлы пакетом» — разнести файлы по строкам реестра по номеру в имени файла;',
        '• «Шаблон» — пустая книга реестра со списками из справочников;',
        '• «Excel» — та же книга с текущими строками;',
        '• «Передать шаблон» — отправить шаблон подрядчику трансмитталом, не скачивая его;',
        '• «На согласование» — подать реестр заказчику; после согласования план становится базовым;',
        '• «Фильтры» — отбор по дисциплине, статусу и срезам: «Просрочено», «Срок на неделе», «На согласование за неделю», «В работу (AFD) за неделю», «Отстаёт от базового плана», «Без файла», «Без кода»;',
        '• «Сохранённые фильтры» — свои наборы отбора: «Сохранить текущий отбор», применить, «Удалить фильтр»;',
        '• «Изменить выбранные» — правка отмеченных строк разом;',
        '• «Аннулировать» — аннулировать отмеченные строки;',
        '• «Снять» — снять выделение.',
        '## Столбцы состава',
        'Номер, наименование, титул, объект, ревизия, назначение, код, статус, план, прогноз, факт, TRM, файл, замечания, отклонение от базового плана и проектные атрибуты. Отбор в заголовке уходит запросом на сервер, а не отбирает загруженную страницу.',
        '## Жизнь реестра',
        '• черновик — состав правится свободно;',
        '• на согласовании — подан заказчику;',
        '• согласован — план зафиксирован, отклонения считаются от него;',
        '• изменяется — открыт новый круг правок; отмена возможна, пока состав не тронут.',
        '## Как идёт согласование реестра',
        'Кнопка «На согласование» создаёт входящий трансмиттал с единственной позицией — самим реестром названной редакции. Дальше он проходит ТОТ ЖЕ круг, что и пакет документов: входной контроль, волна рецензентов, решение утверждающего, выпуск возвратного трансмиттала. У реестров СВОЯ область матрицы — «Реестры MDR/VDR (согласование)»: состав шагов и SLA настраиваются в ней; пока своя ветка не заведена, реестры идут маршрутом по умолчанию.',
        'Замечания пишутся к реестру и адресуются его строкам — у замечания есть поле «строка реестра». Итог решает код: «принято» — реестр становится «Согласован», у него щёлкает редакция, а план строк фиксируется базовым (от него дальше считается отклонение); «принято с замечаниями» или «не принято» — реестр уходит в «На доработку», подрядчик правит состав и подаёт следующую редакцию.',
        '## Объект и титул',
        'У реестра остаётся объект — значение по умолчанию для строк. Титул проекта принадлежит документу: контракт идёт по нескольким объектам. Дисциплины у реестра нет вовсе — она у каждой строки.',
      ],
      en: [
        'The register of contract documentation. One register per contract: the contract is its business key, and the contractor and dates are inherited from it. The register list is on the left, the content of the selected one on the right.',
        '## Buttons above the register list',
        '• «Create MDR» — a new register: contract, stage, title, document number mask. The «no contract» option creates a register of the owner’s own documentation — the area is set there and goes into its code;',
        '• «MDR card» — the card of the selected register: contract, contractor, stage, area, the mask with a sample number, status, approval and content.',
        '## Buttons above the content',
        '• «New document» — add a row by hand: title, discipline, type, purpose, project title, area and the schedule by stage — a plan and a forecast for each (IFR, IFA, AFD). There is no separate overall plan date: the plan of the stage the document is in becomes one. The stage order and the contract window are checked right away, same as for an imported file;',
        '• «Import» — load the content from a file;',
        '• «Check file» — run the file through the same rules without creating anything: a dry run with a report;',
        '• «Historical register» — migration mode for a running project: past dates, life state and code are accepted as they are;',
        '• «Bulk files» — spread files across register rows by the number in the file name;',
        '• «Template» — an empty register book with dictionary dropdowns;',
        '• «Excel» — the same book with the current rows;',
        '• «Hand over template» — send the template to the contractor by transmittal without downloading it;',
        '• «Submit» — submit the register to the owner; once approved the plan becomes the baseline;',
        '• «Filters» — filter by discipline, status and slices: «Overdue», «Due this week», «To approval this week», «To AFD this week», «Behind the baseline», «No file», «No code»;',
        '• «Saved filters» — your own filter sets: «Save current filter», apply, «Delete filter»;',
        '• «Edit selected» — edit the checked rows at once;',
        '• «Cancel» — void the checked rows;',
        '• «Clear» — clears the selection.',
        '## Content columns',
        'Number, title, project title, area, revision, purpose, code, status, plan, forecast, actual, TRM, file, comments, drift from the baseline and the project attributes. A filter in the header goes to the server as a query instead of filtering the loaded page.',
        '## The register life',
        '• draft — the content is freely editable;',
        '• submitted — handed to the owner;',
        '• approved — the baseline plan is fixed and drift is measured against it;',
        '• amending — a new round of changes; it can be cancelled while the content is untouched.',
        '## How the register approval runs',
        '«Submit» creates an incoming transmittal with a single item — the register itself in its named edition. It then walks the SAME round as a document package: incoming check, the reviewer wave, the approver’s decision and the return transmittal. Registers have a matrix area of their OWN — «MDR/VDR registers (approval)»: its steps and SLA are configured there; until that branch exists, registers follow the default route.',
        'Comments are raised against the register and address its rows — a comment carries a «register row» field. The code decides the outcome: accepted — the register becomes «Approved», its edition ticks and the row plans are fixed as the baseline (drift is measured against it); accepted with comments or not accepted — the register goes back to rework, and the contractor submits the next edition.',
        '## Area and project title',
        'The register keeps an area as the default for its rows. The project title belongs to the document: a contract may span several areas. The register has no discipline at all — it belongs to each row.',
      ],
      zh: [
        '按合同管理的文件登记册。一份合同一个登记册：合同是其业务主键，承包商和期限均继承自合同。左侧为登记册列表，右侧为所选登记册的内容。',
        '## 登记册列表上方的按钮',
        '• 「新建 MDR」——新登记册：合同、阶段、名称、文件编号掩码。选择「无合同」将建立业主自有文件的登记册——同时在此指定区域，它会计入其编号；',
        '• 「MDR 卡片」——所选登记册的卡片：合同、承包商、阶段、区域、掩码及编号示例、状态、审批与内容。',
        '## 内容上方的按钮',
        '• 「新建文件」——手工添加一行：名称、专业、类型、用途、项目标题、区域，以及各阶段的计划与预测（IFR、IFA、AFD）。不再单设总计划日期：文件当前所处阶段的计划即为总计划。阶段先后顺序与合同期限当场校验，与导入文件一致；',
        '• 「导入登记册」——用文件加载内容；',
        '• 「校验文件」——按同样规则试运行而不创建任何数据：空跑并给出报告；',
        '• 「历史登记册」——在建项目迁移模式：过去的日期、状态和代码按原样接受；',
        '• 「批量文件」——按文件名中的编号把文件分发到各行；',
        '• 「模板」——带字典下拉列表的空白登记册表；',
        '• 「Excel」——同一张表并含当前各行；',
        '• 「转交模板」——以传递单把模板发给承包商，无需先下载；',
        '• 「提交审批」——把登记册提交业主；批准后计划成为基准；',
        '• 「筛选」——按专业、状态和切片筛选：「已逾期」「本周到期」「本周待审批」「本周转 AFD」「落后于基准计划」「无文件」「无代码」；',
        '• 「已保存的筛选」——自己的筛选集合：「保存当前筛选」、应用、「删除筛选」；',
        '• 「修改所选」——一次性修改勾选的行；',
        '• 「作废」——作废勾选的行；',
        '• 「取消选择」——清除勾选。',
        '## 内容列',
        '编号、名称、项目标题、区域、版次、用途、代码、状态、计划、预测、实际、TRM、文件、意见、相对基准计划的偏差以及项目属性。标题中的筛选会作为查询发往服务器，而不是只筛选已加载的一页。',
        '## 登记册状态',
        '• 草稿——内容可自由修改；',
        '• 已提交——已交业主审批；',
        '• 已批准——基准计划固定，偏差以此计算；',
        '• 修订中——开启新一轮变更；内容未被改动前可取消。',
        '## 登记册审批如何进行',
        '「提交审批」会创建一份只有一个条目的入向传递单——条目即该版本的登记册本身。随后它走与文件包完全相同的审查环节：入场检查、审查人波次、审批人决定、返回传递单。登记册在矩阵中有自己的专属区域——「MDR/VDR 登记册（审批）」：步骤组成与 SLA 在此配置；在该分支建立之前，登记册沿默认路线进行。',
        '意见针对登记册提出并指向其行——意见带有「登记册行」字段。代码决定结果：无意见接收——登记册变为「已批准」，版本号递增，各行计划固定为基准（偏差以此计算）；带意见接收或未接收——登记册退回修改，承包商提交下一版本。',
        '## 区域与项目标题',
        '登记册保留区域，作为各行的默认值。项目标题属于文件：一份合同可能跨多个区域。登记册本身没有专业——专业属于每一行。',
      ],
    },
  },
  {
    id: 'import',
    icon: 'upload',
    title: { ru: 'Загрузка реестра файлом', en: 'Importing a register', zh: '用文件导入登记册' },
    body: {
      ru: [
        'Состав реестра приходит от подрядчика файлом. Чтобы файл заполняли правильно, система сама его и выдаёт: «Шаблон» — пустая книга по правилам ЭТОГО реестра, «Excel» — та же книга с текущими строками.',
        '## Что в книге',
        '• шапка называет реестр, стадию, подрядчика и ожидаемый вид номера документа;',
        '• колонки со справочными значениями заполняются выбором из списка прямо в ячейке;',
        '• лист «Как заполнять» объясняет каждую колонку словами и перечисляет допустимые значения — код и что он означает;',
        '• заголовки двуязычные: файл заполняют обе стороны;',
        '• колонки проектных атрибутов идут справа — те же, что на экране.',
        '## Порядок',
        '• «Проверить файл» — сухой прогон: строки проверяются, но ничего не создаётся. Отчёт называет каждую отбитую строку и причину;',
        '• «Импорт реестра» — та же проверка и запись прошедших строк;',
        '• «Исторический реестр» — режим переноса: принимаются прошедшие сроки, состояние и код.',
        '## Проверки при загрузке',
        '• номер обязан отвечать маске реестра — иначе строка отбивается с указанием ожидаемого вида;',
        '• стадия строки сверяется со стадией реестра;',
        '• дисциплина, тип, назначение и код проверяются по справочникам проекта;',
        '• плановая дата обязательна и должна попадать в срок контракта;',
        '• титул обязателен, когда он стоит в маске номера;',
        '• повтор номера внутри файла и совпадение с уже заведённым отбиваются отдельно.',
        'Строка, не прошедшая проверку, попадает в отчёт с причиной; остальные загружаются. Импорт, молча пропустивший половину, хуже отказа.',
        'Свой выгруженный реестр загружается обратно без правки шапки — на любом из трёх языков выгрузки.',
      ],
      en: [
        'The register content arrives from the contractor as a file. To have it filled in correctly, the system issues the file itself: «Template» is an empty book built by the rules of THIS register, «Excel» is the same book with the current rows.',
        '## What the book contains',
        '• the header names the register, the stage, the contractor and the expected shape of the document number;',
        '• dictionary columns are filled from a dropdown inside the cell;',
        '• the «How to fill» sheet explains every column in words and lists the allowed values — the code and what it means;',
        '• headers are bilingual: both sides fill the file in;',
        '• project attribute columns follow on the right — the same ones as on the screen.',
        '## The order of work',
        '• «Check file» — a dry run: rows are validated but nothing is created. The report names every rejected row and its reason;',
        '• «Import» — the same checks plus writing the rows that passed;',
        '• «Historical register» — migration mode: past dates, life state and code are accepted.',
        '## Checks on import',
        '• the number must match the register mask — otherwise the row is rejected with the expected shape;',
        '• the row stage is checked against the register stage;',
        '• discipline, type, purpose and code are checked against the project dictionaries;',
        '• the planned date is required and must fall inside the contract window;',
        '• the project title is required when the number mask carries it;',
        '• a number repeated inside the file, or already taken, is reported separately.',
        'A row that fails a check goes into the report with its reason; the rest are loaded. An import that silently skips half of the file is worse than a refusal.',
        'A register exported by the system loads back without editing the header — in any of the three export languages.',
      ],
      zh: [
        '登记册内容由承包商以文件提交。为保证填写正确，文件由系统自身签发：「模板」是按本登记册规则生成的空表，「Excel」是同一张表加上当前各行。',
        '## 文件中包含什么',
        '• 表头写明登记册、阶段、承包商以及文件编号的预期样式；',
        '• 取自字典的列在单元格内以下拉列表填写；',
        '• 「填写说明」工作表用文字解释每一列，并列出允许值——代码及其含义；',
        '• 表头为双语：文件由双方共同填写；',
        '• 项目属性列排在右侧——与界面上的列一致。',
        '## 操作顺序',
        '• 「校验文件」——空跑：只校验各行，不创建任何数据。报告会列出每一被拒行及原因；',
        '• 「导入登记册」——同样的校验，并写入通过校验的行；',
        '• 「历史登记册」——迁移模式：接受过去的日期、状态和代码。',
        '## 导入时的校验',
        '• 编号必须符合登记册掩码，否则该行被拒并给出预期样式；',
        '• 行的阶段与登记册阶段核对；',
        '• 专业、类型、用途和代码按项目字典校验；',
        '• 计划日期必填，且须落在合同期限之内；',
        '• 当掩码包含项目标题时，项目标题必填；',
        '• 文件内编号重复、或与已有编号冲突，会单独报出。',
        '未通过校验的行连同原因进入报告，其余照常导入。默默略过一半内容的导入比直接拒绝更糟。',
        '系统自身导出的登记册可原样导入，无需修改表头——三种导出语言均可。',
      ],
    },
  },
  {
    id: 'numbering',
    icon: 'hash',
    title: { ru: 'Номер документа и маски', en: 'Document numbers and masks', zh: '文件编号与掩码' },
    body: {
      ru: [
        'Номер собирается маской — набором сегментов по порядку. Сегмент берёт значение из источника: постоянная строка, счётчик, дисциплина, тип, стадия, объект, титул, подрядчик, контракт, заказ, год, лист.',
        '## Экран «Администрирование → Кодирование»',
        '• слева список масок: документ, реестр MDR, реестр VDR, трансмиттал по направлениям, лист замечаний, позиция замечания, имя файла;',
        '• «Новая маска документа» — код, наименование, разделитель и сразу состав номера;',
        '• «Сегмент» — добавить сегмент: метка, источник, постоянное значение или ширина счётчика;',
        '• стрелки «Выше» и «Ниже» — порядок сегментов, он же порядок частей номера;',
        '• «Убрать» — снять сегмент;',
        '• «Сохранить состав» — записать состав маски.',
        'Над составом показан образец номера, собранный этой же маской.',
        '## Правила',
        '• маска с уже выпущенными номерами заморожена: заведите новую и переведите реестр на неё — выданные номера не переписываются;',
        '• маска знает свою стадию: маску стадии BD нельзя поставить реестру рабочей документации;',
        '• номер, названный человеком, проверяется по маске — и при заведении строки, и при загрузке файла;',
        '• объект и титул берутся у СТРОКИ: документ получает в номер объект своего титула, а не объект реестра;',
        '• маска ставит титул в номер — значит титул у строки обязателен: подставить его не из чего, у объекта титулов может быть несколько;',
        '• счётчик ведётся в разрезе маски и левой части номера: два реестра с разными масками нумеруются независимо.',
      ],
      en: [
        'A number is built by a mask — an ordered set of segments. A segment takes its value from a source: a constant, a counter, the discipline, type, stage, area, project title, contractor, contract, purchase order, year or sheet.',
        '## The «Administration → Coding» screen',
        '• the mask list on the left: document, MDR register, VDR register, transmittal by direction, comment sheet, comment item, file name;',
        '• «New document mask» — code, name, separator and the number composition right away;',
        '• «Segment» — add a segment: label, source, a constant value or the counter width;',
        '• the «Up» and «Down» arrows — the segment order, which is the order of the number parts;',
        '• «Remove» — drops a segment;',
        '• «Save composition» — writes the mask composition.',
        'A sample number built by the same mask is shown above the composition.',
        '## Rules',
        '• a mask with issued numbers is frozen: create a new one and move the register to it — issued numbers are never rewritten;',
        '• a mask knows its stage: a BD-stage mask cannot be used by a detailed-design register;',
        '• a number given by a person is validated against the mask — both when creating a row and when importing a file;',
        '• the area and the title come from the ROW: a document carries the area of its own title, not the register area;',
        '• if the mask carries the title, the row must name it: there is nothing to derive it from, as an area may have several titles;',
        '• the counter runs per mask and per left-hand part of the number: two registers with different masks are numbered independently.',
        '## The document file name',
        'The name of an uploaded file is checked by its own mask — and built by the same editor as the numbers. Its sources are its own: the document number, the revision, the file role and a constant. There is no counter: this mask does not issue a number, it checks a name.',
        '• «Extension» is a separate field, not a segment: the parts of the name are joined by the mask separator, while the extension is appended with a dot;',
        '• the document number is required in the mask: a name that does not carry it cannot be tied to a register row;',
        '• «File role» substitutes the word for the role chosen at upload: PDF for the main file, a supplement or a source file for the others;',
        '• a source file always keeps its own extension — a DWG need not pretend to be a PDF: only the name stem is compared;',
        '• a mask with no segments turns the name check off without deleting the row itself.',
        'The expected name is shown in the document card above the upload button — the server computes it by the very rule it checks against.',
      ],
      zh: [
        '编号由掩码生成——即按顺序排列的一组段。每段的取值来源可以是常量、流水号、专业、类型、阶段、区域、项目标题、承包商、合同、采购订单、年份或图号。',
        '## 「系统管理 → 编码」界面',
        '• 左侧为掩码列表：文件、MDR 登记册、VDR 登记册、按方向的传递单、意见清单、意见条目、文件名；',
        '• 「新建文件掩码」——代码、名称、分隔符，并可直接设定编号组成；',
        '• 「新增段」——添加一段：标签、来源、常量值或流水号位数；',
        '• 「上移」「下移」箭头——段的顺序，即编号各部分的顺序；',
        '• 「移除」——删除该段；',
        '• 「保存组成」——保存掩码的组成。',
        '组成上方显示由同一掩码生成的编号示例。',
        '## 规则',
        '• 已签发过编号的掩码被冻结：请新建掩码并将登记册切换过去——已发出的编号绝不改写；',
        '• 掩码带有自身阶段：BD 阶段的掩码不能用于施工图阶段的登记册；',
        '• 人工填写的编号会按掩码校验——新建行和导入文件时都校验；',
        '• 区域与项目标题取自「行」：文件在编号中带的是自身标题的区域，而不是登记册的区域；',
        '• 若掩码含项目标题，则该行必须指定标题：无从推导，因为一个区域可能有多个标题；',
        '• 流水号按掩码及编号左侧部分分别计数：使用不同掩码的两个登记册各自独立编号。',
        '## 文件名',
        '上传文件的文件名由专属掩码校验——并由与编号相同的编辑器生成。它的来源也是专属的：文件编号、版次、文件角色与常量。没有流水号：该掩码不签发编号，只校验名称。',
        '• 「扩展名」是单独的字段而非段：名称各部分以掩码分隔符连接，扩展名则以点号追加；',
        '• 掩码中必须含文件编号：不带编号的名称无法与登记册行关联；',
        '• 「文件角色」按上传时所选角色代入相应词：主文件为 PDF，其余为附属文件或源文件；',
        '• 源文件始终保留自身扩展名——DWG 无需伪装成 PDF：仅比对名称主干；',
        '• 不含任何段的掩码即关闭名称校验，而无需删除该行本身。',
        '预期文件名显示在文件卡片的上传按钮上方——由服务端按其校验所用的同一规则计算。',
      ],
    },
  },
  {
    id: 'doc',
    icon: 'file-text',
    title: { ru: 'Карточка документа', en: 'Document card', zh: '文件卡片' },
    body: {
      ru: [
        'Карточка собирает всё о документе: реквизиты, сроки, файлы, ревизии, замечания и связи. Открывается щелчком по строке реестра.',
        '## Вкладки',
        '• «Сведения» — реквизиты, стороны, сроки;',
        '• «Атрибуты проекта» — свои колонки заказчика: тег оборудования, дата задания и прочие;',
        '• «Контроль сроков» — план, прогноз и факт по каждой стадии круга: IFR, IFA, AFD;',
        '• «Замечания (CRS / ACSR)» — листы замечаний по ревизиям и ответы;',
        '• «Ревизии» — история ревизий и версий, история передач, история правок карточки;',
        '• «Файлы» — вложения ревизии: основной файл и приложения;',
        '• «Связи документа» — ссылки на другие документы; «Снять связь» убирает одну.',
        '## Кнопки',
        '• «Изменить карточку» — наименование, дисциплина, тип, назначение, лист, рецензент, критичность, титул и объект;',
        '• «Просмотр и разметка» — открыть документ в просмотрщике с замечаниями;',
        '• «Приостановить документ» — работа встала: документ перестанет числиться просроченным;',
        '• «Вернуть в работу» — снять приостановку;',
        '• «Аннулировать документ» — строка больше не в работе;',
        '• «Вернуть документ в состав реестра» — отмена аннулирования;',
        '• «Отменить правки» — сбросить незаписанные изменения формы.',
        '## Удаление файла из хранилища',
        'В строке версии на вкладке «Ревизии» у администратора есть «Удалить файл из хранилища» — рядом со «Снять файл», но это не то же самое: снятие убирает строку, а объект в хранилище остаётся, здесь же стираются сами байты. Это единственное место в системе, где что-либо исчезает физически, поэтому право на него — администрирование, а не «изменять реестр».',
        'Причина обязательна. После удаления от файла остаётся ровно одна вещь — запись журнала, и запись без причины не объясняет ничего ни через год, ни на разборе.',
        'Файл, уехавший трансмитталом, требует второго подтверждения. Он у второй стороны на руках, и стирание у себя означает расхождение с тем, что она получила: молча так делать нельзя. Запрещать вовсе — тоже: убрать требуют как раз то, что уже уехало.',
        '## Правила',
        'Титул задаёт объект: у титула объект ровно один. Номер при правке титула НЕ пересобирается: он идентификатор — стоит в трансмитталах, письмах и именах файлов. Перенумерация и перенос в другой реестр — отдельные поля, и оба пишутся в журнал аудита.',
        'Сроки ведут по стадиям круга. Колонки реестра показывают срок ТЕКУЩЕЙ стадии — ушедший на IFA документ не показывает сроки пройденного IFR. Историческую дату в прошлом вправе поставить только администратор.',
      ],
      en: [
        'The card gathers everything about a document: attributes, dates, files, revisions, comments and links. It opens by clicking a register row.',
        '## Tabs',
        '• «Details» — attributes, parties, dates;',
        '• «Project attributes» — the owner’s own columns: equipment tag, task date and others;',
        '• «Date control» — plan, forecast and actual for each review stage: IFR, IFA, AFD;',
        '• «Comments (CRS / ACSR)» — comment sheets per revision and the replies;',
        '• «Revisions» — the history of revisions and versions, of handovers and of card edits;',
        '• «Files» — the attachments of a revision: the main file and the appendices;',
        '• «Document links» — references to other documents; «Unlink» removes one.',
        '## Buttons',
        '• «Edit card» — title, discipline, type, purpose, sheet, reviewer, criticality, project title and area;',
        '• «View and mark up» — opens the document in the viewer with its comments;',
        '• «Put on hold» — work has stopped: the document stops counting as overdue;',
        '• «Back to work» — releases the hold;',
        '• «Void document» — the row is no longer in progress;',
        '• «Return to the register» — undoes the voiding;',
        '• «Discard changes» — drops the unsaved form changes.',
        '## Deleting a file from storage',
        'On a version row of the «Revisions» tab an administrator has «Delete the file from storage» — next to «Remove file», but not the same thing: removing withdraws the row and leaves the object in storage, this erases the bytes themselves. It is the only place in the system where anything disappears physically, so the permission for it is administration rather than «edit the register».',
        'The reason is required. After the deletion one audit record is all that is left of the file, and a record without a reason explains nothing — not a year later, not at a hearing.',
        'A file that has gone out by transmittal needs a second confirmation. The other party has it in hand, and erasing it here means a divergence from what they received: doing that silently is not allowed. Forbidding it outright is not allowed either — what has already gone out is exactly what people ask to remove.',
        '## Rules',
        'The title sets the area: a title has exactly one area. Editing the title does NOT rebuild the number: it is an identifier — it appears in transmittals, letters and file names. Renumbering and moving to another register are separate fields, and both are written to the audit log.',
        'Dates are kept per review stage. Register columns show the date of the CURRENT stage — a document already at IFA does not show the dates of the IFR round. Only an administrator may set a historical date in the past.',
      ],
      zh: [
        '卡片汇集文件的一切：属性、期限、文件附件、版次、意见和关联。点击登记册中的行即可打开。',
        '## 页签',
        '• 「详情」——属性、相关方、期限；',
        '• 「项目属性」——业主自有的列：设备位号、任务日期等；',
        '• 「期限监控」——各审查阶段（IFR、IFA、AFD）的计划、预测与实际；',
        '• 「意见（CRS / ACSR）」——按版次的意见清单及回复；',
        '• 「版次」——版次与提交次历史、传递历史、卡片修改历史；',
        '• 「文件附件」——某版次的附件：主文件与附属文件；',
        '• 「文件关联」——与其他文件的关联；「解除关联」可删除其中一条。',
        '## 按钮',
        '• 「修改卡片」——名称、专业、类型、用途、图号、审查人、关键性、项目标题和区域；',
        '• 「查看与批注」——在查看器中打开文件及其意见；',
        '• 「暂停文件」——工作已停止：该文件不再计为逾期；',
        '• 「恢复工作」——解除暂停；',
        '• 「作废文件」——该行不再处于工作中；',
        '• 「恢复到登记册」——撤销作废；',
        '• 「撤销修改」——放弃表单中尚未保存的改动。',
        '## 从存储中删除文件',
        '在「版次」页签的某个版本行上，管理员可以使用「从存储中删除文件」——它就在「撤下文件」旁边，但并非同一回事：撤下只是撤销该行，对象仍留在存储中，而这里抹除的是字节本身。这是系统中唯一会让内容真正消失的地方，因此所需权限是「系统管理」，而不是「编辑登记册」。',
        '必须填写原因。删除之后，这个文件只剩下一条审计记录；没有原因的记录，无论一年之后还是在追责会上，都解释不了任何事情。',
        '已随传递单发出的文件需要二次确认。对方手中已有该文件，在己方抹除意味着与对方所收到的内容不一致：这种事不能悄悄做。但也不能一概禁止——人们要求删除的，往往正是已经发出去的东西。',
        '## 规则',
        '项目标题决定区域：一个标题对应且仅对应一个区域。修改标题不会重建编号：编号是标识符——它出现在传递单、函件和文件名中。重新编号与转入其他登记册是单独的字段，且两者都会写入审计日志。',
        '期限按审查阶段维护。登记册的列显示「当前」阶段的日期——已进入 IFA 的文件不再显示已完成的 IFR 日期。仅管理员可以填写过去的历史日期。',
      ],
    },
  },
  {
    id: 'life',
    icon: 'activity',
    title: { ru: 'Состояния документа', en: 'Document statuses', zh: '文件状态' },
    body: {
      ru: [
        'Состояние документа ставит документооборот, а не человек: его двигают подача трансмитталом, рассмотрение и приёмка. Руками задаются только приостановка, замена и аннулирование — и каждое требует причины.',
        '## Круг',
        '• «Запланирован» — строка заведена в реестре. С неё круг начинается, и из неё документ подаётся;',
        '• «Получен по TRM» — подрядчик подал документ трансмитталом;',
        '• «На рассмотрении» — входной контроль закрыт, круг открыт, рецензенты работают;',
        '• «Замечания выпущены (CRS)» — по документу написано хотя бы одно замечание;',
        '• «Ожидает переиздания» — код C, «не принято». Работать по документу нельзя, ждём следующую версию;',
        '• «Принят с замечаниями» — код B. Работать по документу МОЖНО, но замечания учитываются следующей версией;',
        '• «Принят — ожидается AFD» — код A на подаче в IFR или IFA. Документ принят, но по схеме назначений подрядчик обязан подать чистовую версию в назначении AFD;',
        '• «Согласован» — код A на подаче в AFD, финал схемы назначений. Версии больше не щёлкаются; продолжение — только новой ревизией.',
        '## Вне круга',
        '• «Приостановлен» — работа встала, документ из состава не выброшен и просроченным не числится. Возврат отдаёт то состояние, с которого работа встала;',
        '• «Заменён другим документом» — вместо него выпущен другой номер;',
        '• «Аннулирован» — строка выведена из состава. Возврат в состав ставит «Запланирован».',
        '## Ревизия и версия — разное',
        'ВЕРСИЯ — номер подачи внутри ревизии. Она щёлкает на каждой повторной подаче: и после кода C, и после кода B. Ревизия при этом не меняется.',
        'Подача пишется рядом с ревизией: «00 v01» — ревизия 00, вторая присылка. Так подписаны замечания, листы CRS и наборы файлов в карточке. Счёт с нуля: первая присылка — «v00».',
        'Номер подаче даёт ОТПРАВКА, а не загрузка. Пока трансмиттал не ушёл, набор файлов в карточке помечен «Черновик»: подрядчик волен его править и снимать. Отправили — родилась подача, и файлы больше не снимаются: они уже у другой стороны.',
        'РЕВИЗИЯ — выпуск документа. Она растёт ровно на одном событии — приёмке в AFD по коду A. Вместе с ней версия считается заново, код прежнего круга снимается, и документ возвращается в «Запланирован».',
        'Отсюда правило: получив код B, подрядчик учитывает замечания, прикладывает новый файл к ТЕКУЩЕЙ ревизии и подаёт её снова. Кнопка «Выпустить ревизию» появляется только у согласованного документа.',
        '## Схема назначений: куда идёт следующая подача',
        'Назначение выпуска больше не ставится свободно — его диктует код прошлого круга. Первая подача всегда идёт в IFR. Код A на IFR открывает чистовую подачу в AFD; код B уводит в цикл IFA — он повторяется до кода A; код C оставляет в IFR. Код A на подаче в AFD даёт статус «Согласован» — это единственный путь к нему.',
        'Меню подачи у подрядчика показывает только допустимые переходы — например «IFR → IFA» — и считает их сервер по схеме. Схему переходов правит администратор в справочнике «Назначения выпуска»: там же она нарисована графом. Назначения вне схемы (IFI, SUP, CAN…) остаются свободными.',
        '## Шаг перевода: пакетом, а не по одному',
        'На шаге «Перевод замечаний» есть «Выгрузить» и «Загрузить перевод». Выгрузка даёт файл со всеми замечаниями пакета: код, документ, критичность, русский текст и колонку для перевода. Уже переведённое в нём тоже стоит — видно, что сделано.',
        'Файл ключуется КОДОМ замечания, а не номером строки: его можно сортировать, фильтровать и заполнять в несколько заходов. Отчёт построчный — одна негодная строка не отменяет остальные переводы. Замечание чужого пакета отбивается с отметкой.',
        '## Ответы на замечания держат следующую подачу',
        'Подрядчик не подаст следующую версию, пока на замечания прошлого круга нет ОТВЕТОВ. Требуется именно ответ, а не закрытие. Закрывает замечание утверждающий — и его автор, но только на НОВОМ круге: подрядчик подал следующую версию и ответил, и проверить исправление способен тот, кто замечание написал. Своё замечание текущего круга автор не закрывает — на него ещё не отвечали. Справочные замечания («ответа не ждём») подаче не мешают.',
        '## Сравнение версий: замечания рядом',
        'В режиме «Сравнение» справа стоит полка замечаний по документу. Каждое подписано подачей, на которой выпущено — «00 v01», — а две сравниваемые стороны помечены «было» и «стало». Замечание другого круга показано тускло: оно не про этот разговор.',
        'Оттуда же замечание закрывает утверждающий — и автор на замечаниях прошлого круга. «На основе» переносит текст на НОВУЮ версию и открывает обычный вид: место на листе у нового замечания своё, и булавку ставит человек.',
        'Утверждающему в той же полке доступна правка формулировок ПАЧКОЙ: он сводит замечания рецензентов в один лист, и лист уходит за его подписью. Сохраняется целиком — отказ по одной строке отменяет всю правку.',
        '## Очередь рассмотрения: по стадиям',
        'Очередь отбирается стадией круга: «Запустить» — пакет пришёл, но волна не открыта (входной контроль, назначение маршрута); «Идёт» — рецензенты работают; «Завершены» — круг окончен. Отбор считает сервер, а не показанная страница.',
        '## Сравнение — рабочее место, а не смотровая площадка',
        'В режиме «Сравнение», вид «Рядом», на СТАРОЙ версии видны её метки: что было сказано и где именно. Новая при этом чистая — и это ответ на главный вопрос круга: учтено замечание или нет. Слева полка с замечаниями той подачи, справа — выпуск нового.',
        'Замечание ставится только на версию, КОТОРУЮ РАССМАТРИВАЮТ, — ту, что открыта в просмотрщике. Выбрали в «Стало» другую — правая сторона только смотрит и говорит об этом: замечание на архивную версию подрядчик не увидит вовсе.',
        'Метки показаны только в виде «Рядом». В «Наложении» и «Различиях» лист составлен из двух, и метка на нём указывала бы на место, которого нет ни в одной из версий.',
        '## Excel и Word — предпросмотр, а не рассмотрение',
        'Вспомогательные файлы — расчёт, ведомость, пояснительная записка — открываются прямо в просмотрщике, скачивать не нужно. У книги Excel переключаются листы, у формул показано посчитанное значение. Документ Word показан текстом с таблицами и списками.',
        'Замечаний по ним НЕТ, и это не упущение: замечание привязано к листу и месту на нём, а у таблицы и записки ни листов, ни координат. Рассматривают основной файл — PDF; вспомогательные смотрят рядом. Оформление, диаграммы и колонтитулы предпросмотр не переносит — за точным видом «Скачать».',
        'Старый двоичный «.doc» и чертёж «.dwg» показать нечем: их не читает ни одна библиотека браузера. Такие файлы по-прежнему предлагают скачать. Если файл не разобрался, система говорит об этом прямо и оставляет скачивание — пустой лист вместо файла читался бы как поломка.',
        '## Что с чем связано',
        'Готовность считается по состоянию: «Запланирован» — 0 %, «Получен» — 20 %, «На рассмотрении» — 40 %, «Замечания» и «Ожидает переиздания» — 55 %, «Принят с замечаниями» — 85 %, «Принят — ожидается AFD» — 90 %, «Согласован» — 100 %. Веса правит администратор.',
        'Контроль копий смотрит туда же: файл документа в состояниях «Принят с замечаниями», «Принят — ожидается AFD» и «Согласован» выдаётся действующим, в остальных — со штампом «не для производства работ».',
        'Назначение выпуска (IFR, IFA, AFD) не правится, пока идёт круг: оно предмет поданного пакета. Меняется оно следующей подачей — и только на то, что разрешает схема назначений.',
      ],
      en: [
        'The workflow sets the document status, not a person: submissions, reviews and acceptance move it. Only hold, supersede and void are set by hand — and each of them requires a reason.',
        '## The review round',
        '• «Planned» — the row exists in the register. The round starts here, and the document is submitted from here;',
        '• «Received by TRM» — the contractor submitted the document with a transmittal;',
        '• «In review» — the incoming check is closed, the round is open, reviewers are working;',
        '• «Comments issued (CRS)» — at least one comment has been raised on the document;',
        '• «Awaiting reissue» — code C, «not accepted». The document cannot be worked from; the next version is expected;',
        '• «Accepted with comments» — code B. The document CAN be worked from, but the comments are addressed in the next version;',
        '• «Accepted — AFD pending» — code A on an IFR or IFA submission. The document is accepted, but the purpose scheme requires a clean submission as AFD;',
        '• «Approved» — code A on an AFD submission, the final of the purpose scheme. Versions stop ticking; the only way forward is a new revision.',
        '## Outside the round',
        '• «On hold» — work has stopped; the document stays in scope and does not count as overdue. Releasing the hold restores the status work stopped at;',
        '• «Superseded» — another number has been issued in its place;',
        '• «Void» — the row is out of scope. Returning it to the register sets «Planned».',
        '## Revision and version are different things',
        'A VERSION is the submission number inside a revision. It ticks on every resubmission — after code C and after code B alike. The revision does not change.',
        'The submission is written next to the revision: «00 v01» — revision 00, second delivery. Comments, CRS sheets and file sets in the card are all labelled this way. Counting starts at zero: the first delivery is «v00».',
        'A submission number is given by the SENDING, not by the upload. Until the transmittal has gone, the file set in the card is marked «Draft»: the contractor may edit and remove it. Once sent, the submission is born and the files can no longer be removed — the other side already has them.',
        'A REVISION is an issue of the document. It grows on exactly one event — acceptance into AFD by code A. With it the version restarts, the code of the previous round is cleared, and the document goes back to «Planned».',
        'Hence the rule: on code B the contractor addresses the comments, attaches a new file to the CURRENT revision and submits it again. The «Issue revision» button appears only on an approved document.',
        '## The purpose scheme: where the next submission goes',
        'The purpose of issue is no longer free-form — the code of the previous round dictates it. The first submission always goes as IFR. Code A on IFR opens the clean AFD submission; code B sends the document into the IFA loop, repeated until code A; code C keeps it in IFR. Code A on an AFD submission grants «Approved» — the only path to that status.',
        'The contractor’s submission menu shows only the allowed transitions — e.g. «IFR → IFA» — computed by the server from the scheme. An administrator edits the scheme in the «Issue purposes» dictionary, where it is drawn as a graph. Purposes outside the scheme (IFI, SUP, CAN…) stay unrestricted.',
        '## The translation step: in bulk, not one by one',
        'The «Translation» step has «Export» and «Import translation». The export gives a file with every finding of the package: code, document, criticality, the Russian text and a column for the translation. What is already translated is shown too — so the translator sees what is done.',
        'The file is keyed by the finding CODE, not by the row number: it may be sorted, filtered and filled in several passes. The report is per row — one bad row does not cancel the rest. A finding from another package is refused with a note.',
        '## Answers hold the next submission',
        'The contractor cannot submit the next version until the findings of the previous round have ANSWERS. An answer is required, not closure. A finding is closed by the approver — and by its author, but only on a NEW round: the contractor has submitted the next version and answered, and the person who wrote the finding is the one able to verify the fix. The author cannot close a finding of the current round — nobody has answered it yet. Findings marked «no answer expected» do not hold the submission.',
        '## Comparing versions: comments alongside',
        'In «Compare» mode a shelf of the document comments stands on the right. Each is labelled with the submission it was raised on — «00 v01» — and the two compared sides are marked «before» and «after». A comment from another round is dimmed: it is not part of this conversation.',
        'A comment is closed from there too — by the approver, and by the author on comments of the previous round. «Reissue» carries the text over to the NEW version and opens the ordinary view: a new comment has a place of its own on the sheet, and the pin is placed by hand.',
        'The approver gets bulk wording edits in the same shelf: they merge the reviewers\' comments into one sheet, and the sheet goes out over their signature. It is saved as one — a refusal on any row cancels the whole edit.',
        '## The review queue: by stage',
        'The queue is filtered by the stage of the round: «To start» — the package has arrived but the wave is not open (intake, route assignment); «Running» — reviewers are at work; «Done» — the round is over. The filter is computed by the server, not over the page on screen.',
        '## Compare is a workplace, not a viewing gallery',
        'In «Compare», view «Side», the OLD version shows its own marks: what was said and exactly where. The new one stays clean — and that is the answer to the central question of the round: has the comment been addressed. The shelf on the left holds the comments of that submission, the one on the right raises new ones.',
        'A comment is placed only on the version UNDER REVIEW — the one open in the viewer. Pick another one in «After» and the right side only looks and says so: the contractor would never see a comment raised on an archived version.',
        'Marks are shown in the «Side» view only. In «Overlay» and «Diff» the sheet is made of two, and a mark on it would point at a place that exists in neither version.',
        '## Excel and Word — preview, not review',
        'Supporting files — a calculation, a schedule, an explanatory note — open right in the viewer, with no download. An Excel workbook lets you switch sheets, and formulas show their computed value. A Word document is shown as text with its tables and lists.',
        'There are NO comments on them, and that is not an omission: a comment is bound to a sheet and a spot on it, and a table or a note has neither sheets nor coordinates. The main file — the PDF — is what gets reviewed; the supporting files are read alongside it. Formatting, charts and headers are not carried over — use «Download» for the exact look.',
        'The old binary «.doc» and drawings «.dwg» cannot be shown at all: no browser library reads them. Such files are still offered for download. If a file fails to parse, the system says so plainly and keeps the download — a blank sheet instead of a file would read as a breakage.',
        '## What follows from the status',
        'Progress is counted from it: «Planned» — 0 %, «Received» — 20 %, «In review» — 40 %, «Comments» and «Awaiting reissue» — 55 %, «Accepted with comments» — 85 %, «Accepted — AFD pending» — 90 %, «Approved» — 100 %. An administrator edits the weights.',
        'Controlled copies look at the same thing: in «Accepted with comments», «Accepted — AFD pending» and «Approved» the file is issued as in force, otherwise it carries a «not for construction» notice.',
        'The purpose of issue (IFR, IFA, AFD) cannot be edited while the round is open: it is what the submitted package is about. It changes with the next submission — and only to what the purpose scheme allows.',
      ],
      zh: [
        '文件状态由文档流转设定，而非人工填写：提交传递单、审查与接收都会推动它。仅暂停、替代与作废由人工设置，且每项都需填写原因。',
        '## 审查环节',
        '• 「已计划」——该行已在登记册中建立。审查从这里开始，文件也从这里提交；',
        '• 「已按 TRM 接收」——承包商已用传递单提交文件；',
        '• 「审查中」——入场检查已关闭，审查已开启，审查人正在工作；',
        '• 「已出具意见（CRS）」——该文件上至少已提出一条意见；',
        '• 「等待重新签发」——代码 C，「未接收」。不可依据该文件施工，等待下一提交次；',
        '• 「带意见接收」——代码 B。「可以」依据该文件施工，但意见须在下一提交次中落实；',
        '• 「已接收——待 AFD」——在 IFR 或 IFA 提交上获得代码 A。文件已被接收，但按签发用途流转方案，承包商须以 AFD 用途提交定稿版本；',
        '• 「已批准」——在 AFD 提交上获得代码 A，即流转方案的终点。提交次不再递增；唯一的后续是签发新版次。',
        '## 审查环节之外',
        '• 「已暂停」——工作已停止，文件仍在范围内且不计为逾期。恢复时回到停止时的状态；',
        '• 「已被替代」——已用另一编号签发文件取代它；',
        '• 「已作废」——该行退出范围。恢复到登记册后回到「已计划」。',
        '## 版次与提交次不是一回事',
        '「提交次」是同一版次内的提交序号。每次重新提交都会递增——代码 C 之后与代码 B 之后同样如此。版次此时不变。',
        '提交次写在版次旁边：「00 v01」——版次 00，第二次报送。意见、CRS 清单以及卡片中的文件集均按此标注。从零开始计数：首次报送为「v00」。',
        '提交次的编号由「发送」赋予，而非上传。传递单尚未发出前，卡片中的文件集标记为「草稿」：承包商可以修改和删除。一经发出，提交次即产生，文件不可再删除——对方已经收到。',
        '「版次」是文件的一次签发。它只在一个事件上增长——按代码 A 接收进入 AFD。随之提交次重新计数，上一轮的代码被清除，文件回到「已计划」。',
        '由此得出规则：拿到代码 B 后，承包商落实意见，将新文件附到「当前」版次并再次提交。「签发新版次」按钮只在已批准的文件上出现。',
        '## 签发用途流转方案：下一次提交去向何方',
        '签发用途不再自由填写——由上一轮的代码决定。首次提交始终以 IFR 用途进行。IFR 上的代码 A 开启 AFD 定稿提交；代码 B 将文件带入 IFA 循环，直到获得代码 A；代码 C 使其停留在 IFR。AFD 提交上的代码 A 授予「已批准」状态——这是获得该状态的唯一途径。',
        '承包商的提交菜单只显示允许的流转——例如「IFR → IFA」——由服务器按方案计算。管理员在「签发用途」字典中维护方案，并以图形展示。方案之外的用途（IFI、SUP、CAN……）不受限制。',
        '## 翻译环节：批量处理，而非逐条',
        '「意见翻译」环节设有「导出」与「导入翻译」。导出会生成包含该传递单全部意见的文件：编号、文件、关键性、俄文原文以及供填写译文的列。已翻译的内容也会列出——译者可看到已完成的部分。',
        '该文件以意见「编号」为键，而非行号：可以排序、筛选，并分多次填写。报告按行给出——一行不合格不会取消其余译文。属于其他传递单的意见会被拒绝并加以标注。',
        '## 答复决定下一次提交',
        '在上一轮意见得到「答复」之前，承包商无法提交下一版本。要求的是答复而非关闭。意见由审批人关闭，其提出人也可关闭，但仅限于「新一轮」：承包商已提交下一版本并作出答复，而能够核实修改的正是提出该意见的人。提出人不能关闭本轮的意见——尚无人对其作出答复。标注「无需答复」的意见不影响提交。',
        '## 版本比对：意见并列显示',
        '在「比对」模式下，右侧设有该文件的意见栏。每条意见标注其提出时所在的提交次——「00 v01」——并将比对双方标记为「原版」与「新版」。属于其他轮次的意见以淡色显示：它与当前讨论无关。',
        '意见也可从此处关闭——由审批人关闭，提出人亦可关闭上一轮的意见。「基于此条」会将文本转入「新版」并打开常规视图：新意见在图纸上有自己的位置，图钉由人工放置。',
        '审批人在同一栏中可批量修改措辞：他将审查人的意见汇总为一份清单，并以其名义签发。整体保存——任何一行被拒都会取消全部修改。',
        '## 审查队列：按阶段',
        '队列按轮次阶段筛选：「待启动」——传递单已到达但审查波次尚未开启（入口检查、路线指派）；「进行中」——审查人正在工作；「已完成」——本轮已结束。筛选由服务器计算，而非基于当前页面。',
        '## 比对是工作台，而非展示窗',
        '在「比对」模式的「并排」视图下，「旧」版本会显示其自身的标记：说了什么、具体在哪里。新版本保持干净——这正是本轮核心问题的答案：意见是否已落实。左侧栏列出该次提交的意见，右侧栏用于提出新意见。',
        '意见只能提在「正在审查」的版本上——即查看器中打开的那一版。若在「新版」中选择了其他版本，右侧仅供查看并会如实说明：提在存档版本上的意见，承包商根本看不到。',
        '标记仅在「并排」视图中显示。在「叠加」与「差异」视图下图纸由两版合成，其上的标记会指向任一版本都不存在的位置。',
        '## Excel 与 Word——预览，而非审查',
        '辅助文件——计算书、清单、说明书——可直接在查看器中打开，无需下载。Excel 工作簿可切换工作表，公式显示其计算结果。Word 文档以文本形式呈现，并保留表格与列表。',
        '这些文件「不能」提出意见，这并非疏漏：意见须绑定到图纸及其上的具体位置，而表格与说明书既无图纸也无坐标。接受审查的是主文件——PDF；辅助文件供对照阅读。预览不保留排版、图表与页眉页脚——如需精确样式请「下载」。',
        '旧式二进制「.doc」与图纸「.dwg」无法显示：浏览器端没有任何库能读取它们。此类文件仍提供下载。若文件解析失败，系统会如实告知并保留下载——以空白页面代替文件会被读作系统故障。',
        '## 状态决定了什么',
        '进度按状态计算：「已计划」0 %、「已接收」20 %、「审查中」40 %、「已出具意见」与「等待重新签发」55 %、「带意见接收」85 %、「已接收——待 AFD」90 %、「已批准」100 %。权重由管理员维护。',
        '受控副本同样看它：处于「带意见接收」、「已接收——待 AFD」和「已批准」的文件按现行有效签发，其余则带「不用于施工」印章。',
        '签发用途（IFR、IFA、AFD）在审查进行期间不可修改：它是所提交文件包的内容。它随下一次提交而改变——且只能改为流转方案允许的用途。',
      ],
    },
  },
  {
    id: 'viewer',
    icon: 'eye',
    title: { ru: 'Просмотр и разметка', en: 'Viewer and mark-up', zh: '查看与批注' },
    body: {
      ru: [
        'Просмотрщик открывает основной файл ревизии и показывает замечания прямо на листе.',
        '## Панель просмотрщика',
        '• «К реестру» — вернуться к списку, откуда документ открыли;',
        '• «Скачать» — исходный файл ревизии;',
        '• «Предыдущая страница», «Номер страницы», «Следующая страница» — перелистывание; колесо мыши листает, Ctrl + колесо меняет масштаб;',
        '• «Уменьшить» и «Увеличить» — масштаб;',
        '• «Скачать документ с разметкой (PDF)» — весь документ с обводками замечаний;',
        '• «Скачать лист с разметкой (PNG)» — текущий лист картинкой;',
        '• «Перенести обводки незакрытых замечаний с прошлой ревизии» — разметка не рисуется заново после переиздания;',
        '• «Свернуть полку замечаний» и «Развернуть полку замечаний» — боковая полка со списком;',
        '• «Перейти к замечанию на листе» — прокрутка к обводке выбранного замечания;',
        '• «Критичность замечания» — метка в полке.',
        'Замечание ставится обводкой на листе: рамка привязана к странице и координатам, а не к тексту, поэтому работает и со сканами.',
      ],
      en: [
        'The viewer opens the main file of a revision and shows the comments right on the sheet.',
        '## The viewer bar',
        '• «Back to register» — returns to the list the document was opened from;',
        '• «Download» — the source file of the revision;',
        '• «Previous page», «Page number», «Next page» — paging; the mouse wheel scrolls, Ctrl + wheel zooms;',
        '• «Zoom out» and «Zoom in» — the scale;',
        '• «Download the marked-up document (PDF)» — the whole document with the comment frames;',
        '• «Download the marked-up sheet (PNG)» — the current sheet as an image;',
        '• «Carry over the frames of open comments from the previous revision» — the mark-up is not redrawn after a reissue;',
        '• «Collapse the comment shelf» and «Expand the comment shelf» — the side list;',
        '• «Go to the comment on the sheet» — scrolls to the frame of the selected comment;',
        '• «Comment criticality» — the mark in the shelf.',
        'A comment is placed as a frame on the sheet: it is bound to the page and coordinates, not to the text, so it works with scans too.',
      ],
      zh: [
        '查看器打开某版次的主文件，并把意见直接显示在图纸上。',
        '## 查看器工具栏',
        '• 「返回登记册」——回到打开该文件的列表；',
        '• 「下载」——该版次的源文件；',
        '• 「上一页」「页码」「下一页」——翻页；鼠标滚轮翻页，Ctrl + 滚轮缩放；',
        '• 「缩小」和「放大」——比例；',
        '• 「下载带批注的文件（PDF）」——整份文件及意见框；',
        '• 「下载带批注的图纸（PNG）」——当前页另存为图片；',
        '• 「从上一版次继承未关闭意见的批注框」——重新签发后无需重画批注；',
        '• 「收起意见栏」和「展开意见栏」——侧边意见列表；',
        '• 「跳转到图纸上的该意见」——滚动到所选意见的批注框；',
        '• 「意见关键性」——意见栏中的标记。',
        '意见以批注框标注在图纸上：框绑定页码与坐标而非文字，因此扫描件同样适用。',
      ],
    },
  },
  {
    id: 'vdr',
    icon: 'package',
    title: { ru: 'Реестр VDR / PO', en: 'VDR register', zh: 'VDR / PO 登记册' },
    body: {
      ru: [
        'Реестр документации поставщика под заказ: опросные листы, габаритные чертежи, расчёты, руководства.',
        '## Кнопки',
        '• «Создать VDR под PO» — заказ, поставщик, предмет поставки, стадия и маска номера;',
        '• «Карточка VDR» — сведения о заказе: номер PO, поставщик, дисциплина, срок выпуска, просрочка, документы;',
        '• «Проверить файл» — сухой прогон файла состава;',
        '• «Импорт реестра» — загрузка состава;',
        '• «Файлы пакетом» — разнести файлы по строкам;',
        '• «Шаблон» и «Excel» — книга реестра пустая и с текущими строками;',
        '• «Передать шаблон» — отправить шаблон поставщику трансмитталом;',
        '• «Создать документ» — завести строку вручную.',
        'Строка VDR может ссылаться на строку MDR — тогда шапка листа замечаний знает оба номера. Правила номера, проверок и подачи те же, что у MDR.',
      ],
      en: [
        'The register of vendor documentation for a purchase order: data sheets, general arrangement drawings, calculations, manuals.',
        '## Buttons',
        '• «Create VDR for a PO» — the order, vendor, supply subject, stage and number mask;',
        '• «VDR card» — the order details: PO number, vendor, discipline, due date, overdue count, documents;',
        '• «Check file» — a dry run of the content file;',
        '• «Import» — load the content;',
        '• «Bulk files» — spread files across the rows;',
        '• «Template» and «Excel» — the register book empty and with the current rows;',
        '• «Hand over template» — send the template to the vendor by transmittal;',
        '• «New document» — add a row by hand.',
        'A VDR row may reference an MDR row — then the comment sheet header knows both numbers. The rules for numbers, checks and submission are the same as for MDR.',
      ],
      zh: [
        '按采购订单管理的供货商文件登记册：数据表、外形图、计算书、使用手册。',
        '## 按钮',
        '• 「按 PO 新建 VDR」——订单、供货商、供货对象、阶段和编号掩码；',
        '• 「VDR 卡片」——订单信息：PO 编号、供货商、专业、签发期限、逾期数量、文件；',
        '• 「校验文件」——对内容文件进行空跑；',
        '• 「导入登记册」——加载内容；',
        '• 「批量文件」——把文件分发到各行；',
        '• 「模板」和「Excel」——空白登记册表与含当前各行的表；',
        '• 「转交模板」——以传递单把模板发给供货商；',
        '• 「新建文件」——手工添加一行。',
        'VDR 行可引用 MDR 行——这样意见清单的表头同时知道两个编号。编号、校验和提交规则与 MDR 相同。',
      ],
    },
  },
  {
    id: 'trm',
    icon: 'send',
    title: { ru: 'Трансмитталы', en: 'Transmittals', zh: '传递单' },
    body: {
      ru: [
        'Трансмиттал — подача документов между сторонами. Он называет отправителя, получателя, состав и сопроводительный бланк.',
        '## Экран трансмитталов',
        '• вкладки «Все», «Входящие», «Исходящие» — направление;',
        '• «Зарегистрировать входящий» — пакет пришёл почтой, курьером или на диске: заводится вручную;',
        '• «Сформировать TRM» — исходящий пакет: выбрать документы и указать назначение;',
        '• «Журнал в Excel» — выгрузка журнала трансмитталов;',
        '• отбор «показать только пакеты, получение которых не подтверждено».',
        '## Карточка трансмиттала',
        '• «Форма TRM (xlsx)» — бланк заказчика, заполненный данными пакета;',
        '• «Печать» — печатная форма;',
        '• «Скачать» — вложения пакета;',
        '• «Входной контроль» — комплектность, ревизии, исходники и штампы перед рассмотрением;',
        '• «К рассмотрению» — запустить рассмотрение по матрице;',
        '• «Закрыть без рассмотрения» — вернуть пакет подрядчику, не начиная круг: документы уйдут на переиздание, замечания — листом CRS;',
        '• «Дописать документы» — добавить строки в черновик; «Снять позицию из состава черновика» — убрать одну;',
        '• «Править письмо» и «Снять приложение письма» — сопроводительное письмо;',
        '• «Пакет передан вне системы: отметить получение за подрядчика» — отметка о передаче;',
        '• «Аннулировать трансмиттал» — аннулирование;',
        '• «Удалить трансмиттал» — удаление черновика.',
        '## Бланки: входящий и исходящий',
        'Бланк выбирается направлением, и это разные документы заказчика:',
        '• входящий — книга xlsx: шапка со сторонами и номером, блок «Цель выпуска» (лишние строки бланк убирает, остаётся выпущенная), состав строками и блок подтверждения получения;',
        '• исходящий — документ docx: шапка письма (кому, копии, по поручению, № контрагента, заметки), таблица приложений, таблица состава по колонкам бланка и подвал с подписями обеих сторон.',
        'Бланки лежат у сервера и заполняются им: система печатает бланк заказчика, а не похожую на него таблицу.',
        '## Что в пакете «Скачать пакет»',
        '• бланк трансмиттала — тот же, что даёт «Бланк заказчика»;',
        '• файлы состава, разложенные по папкам документов и версий;',
        '• листы замечаний — бланками CRS заказчика, по файлу на лист;',
        '• чертежи с нанесённой разметкой, если замечания обведены на листах;',
        '• опись пакета: перечень передаваемых файлов и перечень передаваемых листов CRS с путями внутри архива.',
        'Файл, который не удалось забрать, из описи не исчезает — он помечен «нет»: молча уменьшившийся архив хуже честной пометки.',
        'Номер трансмиттала собирается своей маской, счётчик ведётся в разрезе подрядчика и направления. Получатель видит только свои пакеты.',
      ],
      en: [
        'A transmittal is a submission of documents between parties. It names the sender, the recipient, the content and the cover form.',
        '## The transmittal screen',
        '• the «All», «Incoming», «Outgoing» tabs — the direction;',
        '• «Register incoming» — a package arrived by post, courier or on a disk: entered by hand;',
        '• «Issue TRM» — an outgoing package: pick the documents and set the purpose;',
        '• «Log to Excel» — exports the transmittal log;',
        '• the «show only packages whose receipt is not confirmed» filter.',
        '## The transmittal card',
        '• «TRM form (xlsx)» — the owner’s form filled with the package data;',
        '• «Print» — the printable form;',
        '• «Download» — the package attachments;',
        '• «Intake control» — completeness, revisions, native files and title blocks before the review;',
        '• «To review» — starts the review by the matrix;',
        '• «Close without review» — returns the package to the contractor without a round: the documents go for reissue, the comments as a CRS sheet;',
        '• «Add documents» — adds rows to a draft; «Remove item from the draft» takes one out;',
        '• «Edit letter» and «Remove the letter attachment» — the cover letter;',
        '• «Handed over outside the system: mark as received for the contractor» — the handover mark;',
        '• «Void transmittal» — voids it;',
        '• «Delete transmittal» — deletes a draft.',
        '## Blanks: incoming and outgoing',
        'The blank follows the direction, and these are two different owner documents:',
        '• incoming — an xlsx book: the header with the parties and the number, the «Issue purpose» block (the blank removes the other lines and keeps the issued one), the content as rows and the receipt block;',
        '• outgoing — a docx document: the letter header (FAO, CC, authority, partner ref, remarks), the attachment table, the content table by the blank columns and the footer with both signatures.',
        'The blanks live on the server and are filled in by it: the system prints the owner’s blank, not a table that looks like one.',
        '## What «Download package» contains',
        '• the transmittal blank — the same one «Customer’s blank» produces;',
        '• the content files, arranged into folders per document and version;',
        '• the comment sheets as the owner’s CRS blanks, one file per sheet;',
        '• the marked-up drawings, when the comments are framed on the sheets;',
        '• the package index: the list of files transmitted and the list of CRS sheets transmitted, with their paths inside the archive.',
        'A file that could not be fetched does not vanish from the index — it is marked «no»: an archive that silently shrank is worse than an honest mark.',
        'The transmittal number is built by its own mask, and the counter runs per contractor and direction. A recipient sees only its own packages.',
      ],
      zh: [
        '传递单是双方之间的文件提交。它写明发件方、收件方、内容清单和封面表单。',
        '## 传递单界面',
        '• 「全部」「来文」「发文」页签——方向；',
        '• 「登记来文」——通过邮寄、快递或光盘收到的批次：手工录入；',
        '• 「生成 TRM」——发出的批次：选择文件并指定用途；',
        '• 「台账导出 Excel」——导出传递单台账；',
        '• 「仅显示未确认接收的批次」筛选。',
        '## 传递单卡片',
        '• 「TRM 表单（xlsx）」——填入本批次数据的业主表单；',
        '• 「打印」——可打印表单；',
        '• 「下载」——本批次附件；',
        '• 「入库检验」——审查前核对齐套性、版次、源文件与图签；',
        '• 「转入审查」——按矩阵启动审查；',
        '• 「不经审查关闭」——不启动审查即退回承包商：文件将重新签发，意见以 CRS 清单发出；',
        '• 「补充文件」——向草稿添加行；「从草稿中移除条目」——删除其中一条；',
        '• 「编辑函件」和「移除函件附件」——随附函件；',
        '• 「批次在系统外交接：代承包商标记为已接收」——交接标记；',
        '• 「作废传递单」——作废；',
        '• 「删除传递单」——删除草稿。',
        '## 表单：来文与发文',
        '表单按方向选择，二者是业主的不同文件：',
        '• 来文——xlsx 工作簿：含相关方与编号的表头、「签发用途」栏（表单会删去其余行，只留已签发的用途）、按行列出的内容以及接收确认栏；',
        '• 发文——docx 文档：函件抬头（收件人、抄送、受托、对方文号、备注）、附件表、按表单列排布的内容表，以及含双方签字的页脚。',
        '表单存放在服务器并由其填写：系统打印的是业主的表单，而不是与之相似的表格。',
        '## 「下载批次」包含什么',
        '• 传递单表单——与「业主表单」按钮生成的相同；',
        '• 内容文件，按文件与版次分文件夹存放；',
        '• 意见清单——业主的 CRS 表单，每张清单一个文件；',
        '• 带批注的图纸（当意见已在图纸上框注时）；',
        '• 批次清单：所传文件目录与所传 CRS 清单目录，并给出在压缩包内的路径。',
        '未能取回的文件不会从清单中消失——它会被标记为「否」：默默变小的压缩包比如实标注更糟。',
        '传递单编号由自身掩码生成，流水号按承包商与方向分别计数。收件方只能看到属于自己的批次。',
      ],
    },
  },
  {
    id: 'review',
    icon: 'clipboard-check',
    title: { ru: 'Рассмотрение', en: 'Review', zh: '审查' },
    body: {
      ru: [
        'Рассмотрение идёт по матрице: маршрут шагов с ролью, исполнителем и сроком. Матрица настраивается администратором и может отличаться по дисциплине и стадии.',
        '## Кнопки на шаге',
        '• «Разметка» — открыть документ в просмотрщике и обвести место замечания;',
        '• «Замечание» — завести замечание: вид, критичность, пункт НТД, текст;',
        '• «Исправить своё замечание, пока шаг не закрыт» и «Снять своё замечание, пока шаг не закрыт» — правка до выпуска;',
        '• «Закрыть шаг» — замечания уйдут утверждающему, изменить их будет нельзя;',
        '• «Передать шаг другому исполнителю» — смена исполнителя;',
        '• «Продлить срок шага» — новый срок; исходный срок остаётся, от него считается отставание;',
        '• «Добавить в идущее рассмотрение ещё одного исполнителя» — утверждение вернётся в ожидание, пока он не закроет свой шаг;',
        '• «Отправить на дополнительное рассмотрение с указанием причины» — ещё один круг.',
        '## Кнопки администратора',
        '• «Перезапустить рассмотрение» — вернуть круг на первый шаг: ход шагов снимается, маршрут и замечания остаются;',
        '• «Остановить рассмотрение» — снять маршрут совсем: состав назначается заново, уже по исправленной матрице; замечания остаются;',
        '• обе кнопки требуют причину, она уходит в журнал; после выпуска ни та, ни другая не работают — там начинается новая ревизия.',
        '## Кнопки утверждающего',
        '• «Поставить код всем документам без кода» — массовое решение;',
        '• «Отклонить замечание» — отклонённое не уйдёт подрядчику и не попадёт в лист;',
        '• «Исправить формулировку до выпуска листа» — правка текста;',
        '• «Подготовить листы CRS» — собрать замечания в листы;',
        '• «Вернуть» — вернуть пакет подрядчику: документы на переиздание, замечания листом CRS.',
        'Код рассмотрения — решение по документу: принят, принят с замечаниями, к доработке, отклонён. Критичное замечание возвращает пакет, не проходя оставшиеся шаги.',
      ],
      en: [
        'Review follows a matrix: a route of steps with a role, an assignee and a due date. The matrix is configured by an administrator and may differ by discipline and stage.',
        '## Buttons on a step',
        '• «Mark up» — opens the document in the viewer to frame the place of a comment;',
        '• «Comment» — creates a comment: kind, criticality, standard clause, text;',
        '• «Edit your comment while the step is open» and «Remove your comment while the step is open» — edits before issue;',
        '• «Close step» — the comments go to the approver and can no longer be changed;',
        '• «Pass the step to another assignee» — changes the assignee;',
        '• «Extend the step due date» — a new date; the original one stays and the lag is measured from it;',
        '• «Add one more assignee to the running review» — approval waits until they close their step;',
        '• «Send for an additional review with a reason» — one more round.',
        '## Administrator buttons',
        '• «Restart the review» — takes the review back to its first step: step progress is cleared, the route and the findings stay;',
        '• «Stop the review» — drops the route entirely: the roster is assigned again from the corrected matrix; the findings stay;',
        '• both ask for a reason, and it goes into the log; after the issue neither works — a new revision starts there.',
        '## Approver buttons',
        '• «Give a code to every document without one» — a bulk decision;',
        '• «Reject the comment» — a rejected comment does not reach the contractor and does not enter the sheet;',
        '• «Fix the wording before the sheet is issued» — edits the text;',
        '• «Prepare CRS sheets» — collects the comments into sheets;',
        '• «Return» — returns the package to the contractor: documents for reissue, comments as a CRS sheet.',
        'The review code is the decision on the document: accepted, accepted with comments, to be revised, rejected. A critical comment returns the package without walking the remaining steps.',
      ],
      zh: [
        '审查按矩阵进行：由角色、执行人和期限组成的步骤路线。矩阵由管理员配置，可按专业和阶段不同。',
        '## 步骤上的按钮',
        '• 「批注」——在查看器中打开文件并框出意见位置；',
        '• 「新增意见」——录入意见：类别、关键性、规范条款、正文；',
        '• 「步骤关闭前修改自己的意见」和「步骤关闭前撤回自己的意见」——签发前可修改；',
        '• 「关闭步骤」——意见提交审批人后不可再修改；',
        '• 「把步骤转交他人」——更换执行人；',
        '• 「延长步骤期限」——设定新期限；原期限保留，滞后按其计算；',
        '• 「向进行中的审查增加一名执行人」——在其关闭步骤前审批处于等待；',
        '• 「附理由转入补充审查」——再走一轮。',
        '## 管理员按钮',
        '• 「重启审查」——将审查退回第一步：步骤进度清零，路线与意见保留；',
        '• 「停止审查」——完全撤销路线：按更正后的矩阵重新分配班底；意见保留；',
        '• 两者均须填写理由，理由写入日志；签发之后两者都不可用——那里开始的是新版次。',
        '## 审批人按钮',
        '• 「为所有无代码的文件统一给代码」——批量决定；',
        '• 「驳回该意见」——被驳回的意见不会发给承包商，也不进入清单；',
        '• 「清单签发前修改表述」——修改正文；',
        '• 「生成 CRS 清单」——把意见汇总成清单；',
        '• 「退回」——将批次退回承包商：文件重新签发，意见以 CRS 清单发出。',
        '审查代码即对文件的决定：批准、带意见批准、需修改、不批准。出现关键意见时，批次不再走完其余步骤即行退回。',
      ],
    },
  },
  {
    id: 'crs',
    icon: 'message-square-warning',
    title: { ru: 'CRS / ACSR', en: 'Comments', zh: 'CRS / ACSR 意见' },
    body: {
      ru: [
        'Замечания ведутся листами: лист CRS относится к документу, его ревизии и версии, и внутри него — пронумерованные позиции. У позиции есть вид (техническое, оформление, комплектность, справочное), критичность, пункт НТД и текст.',
        '## Кнопки',
        '• «Открыть на документе» — перейти к обводке замечания в просмотрщике;',
        '• «Печать» и «Печать листа» — бланк CRS, заполненный данными листа;',
        '• «Назад» и «Вперёд» — соседние листы;',
        '• «Подготовить ответ (ACSR)» — ответ подрядчика на позиции листа;',
        '• «Загрузить бланк CRS с ответами» — ответы из файла лягут на замечания листа;',
        '• «Отклонить замечание» — сторона заказчика снимает позицию с обоснованием.',
        'Справочное замечание закрывается решением заказчика, напоминания по нему не идут. Лист и позиции нумеруются масками — как и документы.',
      ],
      en: [
        'Comments are kept on sheets: a CRS sheet belongs to a document, its revision and version, and holds numbered items. An item has a kind (technical, presentation, completeness, informative), a criticality, a standard clause and a text.',
        '## Buttons',
        '• «Open on the document» — jumps to the comment frame in the viewer;',
        '• «Print» and «Print sheet» — the CRS form filled with the sheet data;',
        '• «Back» and «Forward» — the neighbouring sheets;',
        '• «Prepare a reply (ACSR)» — the contractor’s reply to the sheet items;',
        '• «Upload a CRS form with replies» — replies from a file land on the sheet comments;',
        '• «Reject the comment» — the owner side withdraws an item with a justification.',
        'An informative comment is closed by the owner’s decision and raises no reminders. Sheets and items are numbered by masks — like documents.',
      ],
      zh: [
        '意见以清单管理：一张 CRS 清单对应某文件的某个版次与提交次，清单内为编号条目。每条意见有类别（技术、编制、齐套、参考）、关键性、规范条款和正文。',
        '## 按钮',
        '• 「在文件上打开」——跳转到查看器中的意见批注框；',
        '• 「打印」和「打印清单」——填入清单数据的 CRS 表单；',
        '• 「上一张」和「下一张」——相邻清单；',
        '• 「准备回复（ACSR）」——承包商对清单条目的回复；',
        '• 「上传带回复的 CRS 表单」——文件中的回复将写入清单意见；',
        '• 「驳回该意见」——业主一方附理由撤销该条。',
        '参考性意见由业主决定关闭，且不发送提醒。清单和条目同样由掩码编号——与文件一样。',
      ],
    },
  },
  {
    id: 'handover',
    icon: 'package-check',
    title: { ru: 'Комплекты ИД', en: 'Handover packages', zh: '竣工资料套' },
    body: {
      ru: [
        'Комплект исполнительной документации — срез реестра по титулу: что готово к сдаче и чего не хватает.',
        '## Кнопки',
        '• «Новый комплект» — завести комплект по титулу;',
        '• «Выпустить комплект» — зафиксировать состав;',
        '• «Передать трансмитталом» — отправить комплект получателю;',
        '• «Снять требование» — исключить позицию из требований комплекта;',
        '• «Вернуть» — вернуть снятое требование обратно.',
        'Состав считается по документам титула; готовность — по принятым (AFD) документам с приложенным файлом. Опись комплекта выгружается файлом.',
      ],
      en: [
        'A handover package is a slice of the register by project title: what is ready to hand over and what is missing.',
        '## Buttons',
        '• «New package» — create a package for a title;',
        '• «Issue package» — fixes the content;',
        '• «Hand over by transmittal» — sends the package to the recipient;',
        '• «Drop requirement» — excludes an item from the package requirements;',
        '• «Restore» — brings a dropped requirement back.',
        'The content is computed from the documents of the title; readiness is measured by accepted (AFD) documents with an attached file. The package inventory is exported as a file.',
      ],
      zh: [
        '竣工资料套是登记册按项目标题的切片：哪些已可移交、哪些还缺。',
        '## 按钮',
        '• 「新建资料套」——按项目标题建立资料套；',
        '• 「签发资料套」——固定其内容；',
        '• 「以传递单移交」——把资料套发给收件方；',
        '• 「取消该要求」——把某条从资料套要求中排除；',
        '• 「恢复」——把已取消的要求恢复回来。',
        '内容按该标题下的文件统计；完备度按已批准（AFD）且附有文件的条目计算。资料套清单可导出为文件。',
      ],
    },
  },
  {
    id: 'progress',
    icon: 'layers',
    title: { ru: 'Пакеты работ', en: 'Work packages', zh: '工作包' },
    body: {
      ru: [
        'Разбивка проекта на пакеты работ и прогресс по ним. Прогресс считается по документам пакета и весам стадий.',
        '## Кнопки',
        '• «Новый пакет» — пакет верхнего уровня;',
        '• «Подпакет» — вложенный пакет;',
        '• «Разложить документы» — отобрать документы в пакет; «Убрать из пакета» — снять строку;',
        '• «Веха» — контрольная дата пакета; «Снять веху» — убрать её;',
        '• «Веса стадий» — сколько готовности даёт каждая стадия круга;',
        '• «Изменить» и «Снять» — правка и удаление пакета.',
      ],
      en: [
        'The breakdown of the project into work packages and their progress. Progress is computed from the package documents and the stage weights.',
        '## Buttons',
        '• «New package» — a top-level package;',
        '• «Sub-package» — a nested one;',
        '• «Assign documents» — picks documents into the package; «Remove from package» takes a row out;',
        '• «Milestone» — a control date of the package; «Remove milestone» drops it;',
        '• «Stage weights» — how much completion each review stage contributes;',
        '• «Edit» and «Remove» — edit and delete a package.',
      ],
      zh: [
        '把项目划分为工作包并显示其进度。进度按工作包内文件和阶段权重计算。',
        '## 按钮',
        '• 「新建工作包」——顶层工作包；',
        '• 「子包」——下级工作包；',
        '• 「分配文件」——把文件归入该包；「移出工作包」——移除某行；',
        '• 「里程碑」——工作包的控制日期；「删除里程碑」——取消该日期；',
        '• 「阶段权重」——各审查阶段各自贡献多少完成度；',
        '• 「修改」和「删除」——修改或删除工作包。',
      ],
    },
  },
  {
    id: 'reports',
    icon: 'bar-chart-3',
    title: { ru: 'Отчёты и выгрузки', en: 'Reports', zh: '报表与导出' },
    body: {
      ru: [
        'Отчёты по ходу работ. Любой отчёт выгружается в Excel кнопкой над таблицей.',
        '## Вкладки',
        '• «Бэклог рассмотрения» — что стоит в очереди и у кого;',
        '• «Статус подачи / согласования» — где какие реестры и документы;',
        '• «Эскалация просрочек» — просроченные шаги по исполнителям;',
        '• «Качество подачи» — коды рассмотрения по подрядчикам: основание для письма о качестве;',
        '• «Входной контроль» — возвраты на приёмке: круг, пройденный впустую;',
        '• «Отставание от контракта» — отклонение от контрактного срока.',
        'Параметры задаются панелью над таблицей: период, реестр, дисциплина, подрядчик.',
      ],
      en: [
        'Reports on the progress of work. Every report exports to Excel with the button above the table.',
        '## Tabs',
        '• «Review backlog» — what is queued and with whom;',
        '• «Submission / approval» — where each register and document stands;',
        '• «Overdue escalation» — overdue steps by assignee;',
        '• «Submission quality» — review codes by contractor: the basis for a quality letter;',
        '• «Intake control» — returns at intake: a round walked in vain;',
        '• «Contract lag» — the drift from the contractual date.',
        'The parameters are set in the bar above the table: period, register, discipline, contractor.',
      ],
      zh: [
        '工作进展报表。任何报表都可用表格上方的按钮导出为 Excel。',
        '## 页签',
        '• 「审查积压」——排队事项及其责任人；',
        '• 「提交 / 审批状态」——各登记册与文件所处环节；',
        '• 「逾期升级」——按执行人统计的逾期步骤；',
        '• 「提交质量」——按承包商统计的审查代码：质量函件的依据；',
        '• 「入库检验」——验收环节的退回：白走一轮；',
        '• 「相对合同的滞后」——相对合同期限的偏差。',
        '参数在表格上方的面板中设置：期间、登记册、专业、承包商。',
      ],
    },
  },
  {
    id: 'search',
    icon: 'search',
    title: { ru: 'Поиск', en: 'Search', zh: '检索' },
    body: {
      ru: [
        'Поиск по номеру документа, наименованию, трансмитталу и замечанию. Быстрый поиск в верхней строке открывается по Ctrl + K.',
        '## Кнопки',
        '• «Расширенный поиск» — отбор по дисциплине, статусу и коду;',
        '• «Спросить у сервера больше строк по каждому источнику» — расширить выдачу;',
        '• отметка «Текст получен распознаванием скана» — предупреждение, что в тексте возможны ошибки распознавания.',
        'Поиск по тексту вложенных файлов работает по тем, у которых текст извлечён.',
      ],
      en: [
        'Search across document numbers, titles, transmittals and comments. The quick search in the top bar opens with Ctrl + K.',
        '## Buttons',
        '• «Advanced search» — filter by discipline, status and code;',
        '• «Ask the server for more rows per source» — widens the result set;',
        '• the «text obtained by scan recognition» mark — a warning that the text may contain recognition errors.',
        'Full-text search covers the files whose text has been extracted.',
      ],
      zh: [
        '按文件编号、名称、传递单和意见检索。顶部栏的快速检索可用 Ctrl + K 打开。',
        '## 按钮',
        '• 「高级检索」——按专业、状态和代码筛选；',
        '• 「向服务器索取每个来源的更多结果」——扩大结果集；',
        '• 「文本来自扫描识别」标记——提示该文本可能存在识别错误。',
        '附件全文检索覆盖已提取文本的文件。',
      ],
    },
  },
  {
    id: 'admin',
    icon: 'shield-check',
    title: { ru: 'Администрирование', en: 'Administration', zh: '系统管理' },
    body: {
      ru: [
        'Один экран с вкладками — по тому, чем управляют.',
        '## Пользователи и роли',
        '• «Добавить пользователя» — сотрудник, его роль и дисциплины;',
        '• «Учётка» — вход в систему для сотрудника; «Снять» — отключить доступ;',
        '• «Создать вход» при заведении записи — система заведёт вход сама и один раз покажет временный пароль; при первом входе человек придумает свой;',
        '• «Сбросить пароль» в карточке учётной записи — новый временный пароль, тоже один раз и со сменой при следующем входе;',
        '• «Учётка» в строке организации заводит запись именно этой организации: сторона и компания в форме зафиксированы;',
        '• у заведённой учётки подрядчика сторону и организацию сменить нельзя — для другой организации заводится своя запись;',
        '• в шапке столбцов «Роль», «Сторона» и «Статус» — отбор с числами по всему проекту, а не по странице; там же группировка: «Подрядчик»/«Заказчик» и по статусу.',
        '• «Добавить роль» и «Копировать» — новая роль и роль по образцу;',
        '• «Добавить группу» — группа исполнителей для маршрутов;',
        '• матрица прав по разделам: «нет», «чтение», «чтение и запись». Снятое право сервер начинает отбивать сразу, без пересборки.',
        '## Замещения',
        '• «Завести замещение» — кто отсутствует, кто замещает, с какого числа по какое включительно и почему;',
        '• «Отозвать» — приказ перестаёт действовать досрочно.',
        'Шаги не переназначаются: на срок приказа замещающий получает право вести шаги отсутствующего и его разделы в придачу к своим. Переписанный исполнитель означал бы, что по завершённому кругу уже не сказать, кому шаг был поручен на самом деле.',
        'Заводят двое: администратор — за кого угодно, остальные — только за себя, потому что право подписи передаёт его владелец. Администратора не замещают вовсе: у него не работа в круге, а ключи от системы, и приказ на него был бы способом раздать их на неделю. Отзыв снимает доступ, но приказ не стирает — «кто мог закрыть этот шаг тогда» спрашивают и через год.',
        '## Матрица распределения',
        '• реестр веток: сколько уникальных маршрутов заведено, чем ветка отличается от маршрута дисциплины и от default; строка открывает редактор;',
        '• «Выгрузить все в Excel» — все ветки одним файлом; «Загрузить Excel» в реестре — после предпросмотра заменяет перечисленные в файле ветки целиком и атомарно, не названные не трогает; «Снять ветку» — явное удаление, default снять нельзя;',
        '• маршрут шагов по дисциплине и стадии: действие, роль, исполнитель, срок;',
        '• сроки в трёх колонках по кругам рассмотрения: первая подача, вторая и последующие до кода A; пустая колонка наследует предыдущую. Круг считается по выпущенным решениям, а не по номеру ревизии;',
        '• «Шаблон» — выгрузить маршрут в Excel; «Загрузить Excel» — загрузить обратно;',
        '• «Сбросить» — перечитать с сервера; «Сохранить маршрут» — записать.',
        '## Контракты и справочники',
        '• «Добавить контракт» — подрядчик и сроки; «Изменить организацию» — карточка подрядчика или поставщика;',
        '• «Завести справочник» — свой справочник проекта; «Добавить» — строка справочника; «Снять» — отключить строку;',
        '• справочники: дисциплины, типы, назначения выпуска, коды рассмотрения, состояния, объекты и площадки, титулы проекта. Титул привязан к объекту.',
        '## Атрибуты документа и кодирование',
        '• «Добавить» — своя колонка проекта: текст, число, дата или список из справочника;',
        '• «Снять атрибут» — убрать колонку; значения остаются в истории документов;',
        '• «Новая маска документа», «Сегмент», «Сохранить состав» — см. статью о номерах и масках.',
        '## Прочие вкладки',
        '• «Мои извещения» — какие события присылать вам;',
        '• «Почта» — отправка уведомлений и проверка настроек;',
        '• «Проект» — наименование, объект и «Загрузить производственный календарь на год: дата, вид дня, примечание».',
        '## Журнал аудита',
        'Отдельный раздел прав. «Выгрузить журнал» отдаёт записи в Excel. Записи неизменяемы: их нельзя ни исправить, ни удалить — этого не может и администратор.',
      ],
      en: [
        'A single screen with tabs — one per thing being managed.',
        '## Users and roles',
        '• «Add user» — an employee, their role and disciplines;',
        '• «Login» — system access for an employee; «Disable» — revokes access;',
        '• «Create the login» while adding a record — the system creates the sign-in itself and shows a temporary password once; the person picks their own on first sign-in;',
        '• «Reset password» in the account card — a new temporary password, also shown once and changed on the next sign-in;',
        '• «Login» in an organisation row creates a record for that organisation: the side and the company are fixed in the form;',
        '• an existing contractor account keeps its side and organisation — another organisation gets its own record;',
        '• the «Role», «Side» and «Status» column headers carry a filter with project-wide counts, not page ones, and grouping: contractor/customer and by status.',
        '• «Add role» and «Copy» — a new role and a role from a sample;',
        '• «Add group» — a group of assignees for the routes;',
        '• the permission matrix per area: none, read, read and write. A revoked permission starts being refused by the server immediately, with no rebuild.',
        '## Substitutions',
        '• «Add substitution» — who is away, who deputises, from which date to which one inclusive, and why;',
        '• «Revoke» — the order stops working before its end date.',
        'Steps are not reassigned: for the period of the order the deputy gains the right to run the absent person’s steps, and their sections on top of their own. A rewritten assignee would mean a finished round no longer says who the step was actually given to.',
        'Two kinds of people create one: an administrator for anyone, everybody else only for themselves, because a right is handed over by its owner. An administrator is never substituted: they hold the keys to the system rather than a place in the round, and an order on them would be a way to lend those keys for a week. Revoking withdraws the access but keeps the order — «who could have closed this step back then» is asked a year later too.',
        '## Distribution matrix',
        '• the branch registry: how many unique routes exist and how each branch differs from the discipline route and from default; a row opens the editor;',
        '• «Export all to Excel» — every branch in one file; «Import Excel» on the registry — after a preview, replaces the branches named in the file atomically and leaves the rest intact; «Remove the branch» — an explicit removal, default cannot be removed;',
        '• the route of steps by discipline and stage: action, role, assignee, due days;',
        '• due days come in three columns by review round: first submission, second and further ones until code A; an empty column inherits the previous one. The round is counted by issued decisions, not by the revision number;',
        '• «Template» — exports the route to Excel; «Import Excel» — loads it back;',
        '• «Reset» — re-reads from the server; «Save routing» — writes it.',
        '## Contracts and dictionaries',
        '• «Add contract» — the contractor and the dates; «Edit organisation» — the card of a contractor or vendor;',
        '• «Add dictionary» — a project dictionary of your own; «Add» — a dictionary row; «Disable» — switches a row off;',
        '• dictionaries: disciplines, types, issue purposes, review codes, life states, areas and project titles. A title is bound to an area.',
        '## Document attributes and coding',
        '• «Add» — a project column: text, number, date or a dictionary list;',
        '• «Remove attribute» — drops the column; the values stay in the document history;',
        '• «New document mask», «Segment», «Save composition» — see the article on numbers and masks.',
        '## Other tabs',
        '• «My notifications» — which events are sent to you;',
        '• «Mail» — notification delivery and settings check;',
        '• «Project» — the name, the plant and «Upload the working calendar for a year: date, day type, note».',
        '## Audit log',
        'A separate permission area. «Export log» gives the records as Excel. The records are immutable: they can be neither edited nor deleted — not even by an administrator.',
      ],
      zh: [
        '一个界面，按管理对象分页签。',
        '## 用户与角色',
        '• 「新增用户」——员工及其角色和专业；',
        '• 「登录账号」——员工的系统访问；「停用」——收回访问权限；',
        '• 建档时勾选「创建登录」——系统自动创建登录并一次性显示临时密码；首次登录时本人自行设置新密码；',
        '• 账号卡片中的「重置密码」——发放新的临时密码，同样只显示一次，下次登录必须更改；',
        '• 在组织行中点击「登录账号」创建的是该组织的账号：表单中的身份与公司均已固定；',
        '• 已建立的承包商账号不可更改身份与组织——其他组织需另建账号；',
        '• 「角色」「身份」「状态」列头提供筛选，计数按整个项目而非当前页统计；同处可按承包商／业主及状态分组。',
        '• 「新增角色」和「复制」——新角色或按样板建角色；',
        '• 「新增分组」——用于路线的执行人分组；',
        '• 按板块的权限矩阵：无、只读、读写。撤销的权限服务器立即开始拒绝，无需重新部署。',
        '## 代班安排',
        '• 「新建代班」——谁缺席、谁代理、自何日至何日（含当日）、以及原因；',
        '• 「撤销」——提前终止该代班。',
        '步骤不会被改派：在代班期间，代理人获得处理缺席人步骤的权限，并在自己原有权限之外增加对方的板块权限。若直接改写执行人，已结束的审查环节便再也说不清该步骤当初究竟交给了谁。',
        '可以建立代班的有两类人：管理员可为任何人建立，其余人只能为自己建立，因为权限只能由其本人转交。管理员本人完全不可被代班：他掌握的是系统钥匙，而不是审查环节中的工作，为他建立代班等于把钥匙外借一周。撤销会收回权限但不会删除记录——「当时谁有权关闭这个步骤」这样的问题一年后仍会被问到。',
        '## 分派矩阵',
        '• 按专业和阶段的步骤路线：动作、角色、执行人、期限天数；',
        '• 「模板」——把路线导出为 Excel；「导入 Excel」——重新载入；',
        '• 「重置」——从服务器重新读取；「保存路线」——写入；',
        '• 「撤销修改」——把表单恢复到已保存状态。',
        '## 合同与字典',
        '• 「新增合同」——承包商与期限；「修改组织」——承包商或供货商卡片；',
        '• 「新建字典」——项目自有字典；「新增」——字典条目；「停用」——停用某条；',
        '• 字典包括：专业、类型、签发用途、审查代码、状态、区域与场地、项目标题。标题与区域绑定。',
        '## 文件属性与编码',
        '• 「新增」——项目自有列：文本、数字、日期或取自字典的列表；',
        '• 「删除属性」——移除该列；取值仍保留在文件历史中；',
        '• 「新建文件掩码」「新增段」「保存组成」——详见编号与掩码条目。',
        '## 其他页签',
        '• 「我的通知」——哪些事件发送给您；',
        '• 「邮件」——通知发送与设置检查；',
        '• 「项目」——名称、厂区，以及「上传全年工作日历：日期、日类型、备注」。',
        '## 审计日志',
        '独立的权限板块。「导出日志」把记录导出为 Excel。记录不可更改：既不能修改也不能删除——管理员同样不能。',
      ],
    },
  },
  {
    id: 'contractor',
    icon: 'building-2',
    title: { ru: 'Портал подрядчика', en: 'Contractor portal', zh: '承包商门户' },
    body: {
      ru: [
        'Подрядчик работает в своём наборе разделов: «Мои задачи», «Мой реестр MDR», «Мои документы», «Трансмитталы», «Замечания заказчика».',
        '## Кнопки',
        '• «Сформировать трансмиттал» и «Сформировать TRM» — собрать пакет из выбранных документов;',
        '• «Снять» — убрать документ из черновика пакета;',
        '• «Печать листа» — бланк CRS с замечаниями заказчика;',
        '• «Подготовить ответ (ACSR)» — ответы на позиции листа;',
        '• «Загрузить бланк CRS с ответами: ответы лягут на замечания листа» — ответы из файла;',
        '• в реестре — те же «Импорт реестра», «Проверить файл», «Шаблон», «Excel», «На согласование».',
        'Реестр наполняется, пока он в черновике. Видно только своё: ограничение стоит в выборке на сервере, а не в экране — чужой документ не откроется и по прямой ссылке.',
      ],
      en: [
        'A contractor works in its own set of sections: «My tasks», «My MDR register», «My documents», «Transmittals», «Owner comments».',
        '## Buttons',
        '• «Issue transmittal» and «Issue TRM» — assemble a package from the selected documents;',
        '• «Remove» — takes a document out of the draft package;',
        '• «Print sheet» — the CRS form with the owner’s comments;',
        '• «Prepare a reply (ACSR)» — replies to the sheet items;',
        '• «Upload a CRS form with replies: they land on the sheet comments» — replies from a file;',
        '• in the register — the same «Import», «Check file», «Template», «Excel», «Submit».',
        'The register is filled while it is a draft. Only your own data is visible: the limit lives in the server query, not in the screen — another party’s document will not open even by a direct link.',
      ],
      zh: [
        '承包商在自己的板块中工作：「我的任务」「我的 MDR 登记册」「我的文件」「传递单」「业主意见」。',
        '## 按钮',
        '• 「生成传递单」和「生成 TRM」——用所选文件组成批次；',
        '• 「移除」——把某文件从草稿批次中去掉；',
        '• 「打印清单」——含业主意见的 CRS 表单；',
        '• 「准备回复（ACSR）」——对清单条目的回复；',
        '• 「上传带回复的 CRS 表单：回复将写入清单意见」——来自文件的回复；',
        '• 在登记册中——同样的「导入登记册」「校验文件」「模板」「Excel」「提交审批」。',
        '登记册处于草稿状态时可以填写。只能看到自己的数据：该限制位于服务器查询中而非界面——他人的文件即使用直接链接也打不开。',
      ],
    },
  },
  {
    id: 'support',
    icon: 'life-buoy',
    title: { ru: 'Если что-то не работает', en: 'If something goes wrong', zh: '出现问题时' },
    body: {
      ru: [
        '## Отказ с текстом',
        'Отказы называют причину: «номер не отвечает маске реестра», «значение вне справочника», «стадия строки не совпадает со стадией реестра». Текст отказа — и есть указание, что поправить.',
        '## Кнопка недоступна',
        'Наведите на неё: подсказка называет причину — «укажите объект: он входит в номер документа», «свободных контрактов нет», «в маршруте нужен хотя бы один шаг».',
        '## Раздел не открывается',
        'Ответ «недостаточно прав» означает, что роли не выдан этот раздел. Права правит администратор на вкладке «Роли и доступ».',
        '## Не видно чужих данных',
        'Так и задумано: подрядчик видит только своё, сотрудник — то, что разрешено его роли и его стороне.',
        '## Таблица выглядит не так',
        'Раскладка столбцов личная и живёт в браузере. «Вернуть состав, ширины и порядок столбцов» возвращает её к исходной.',
        '## Что приложить к обращению',
        '• номер документа, реестра или трансмиттала;',
        '• время и текст отказа;',
        '• файл, если отказ пришёл на загрузке.',
        'Каждое действие пишется в журнал аудита, и по нему разбирается любое обращение.',
      ],
      en: [
        '## A refusal with a text',
        'Refusals name the reason: «the number does not match the register mask», «value outside the dictionary», «the row stage differs from the register stage». The text of the refusal is the instruction on what to fix.',
        '## A button is disabled',
        'Hover over it: the tooltip names the reason — «choose the area: it goes into the document number», «no free contracts», «a route needs at least one step».',
        '## A section does not open',
        'A «not enough permissions» answer means the role has not been granted this area. Permissions are edited by an administrator on the «Roles» tab.',
        '## Other parties’ data is not visible',
        'That is by design: a contractor sees only its own data, an employee sees what the role and the side allow.',
        '## The table looks different',
        'The column layout is personal and lives in the browser. «Restore columns, widths and order» brings it back to the original.',
        '## What to attach to a request',
        '• the document, register or transmittal number;',
        '• the time and the text of the refusal;',
        '• the file, if the refusal came from an import.',
        'Every action is written to the audit log, and any request is investigated by it.',
      ],
      zh: [
        '## 带文字的拒绝提示',
        '拒绝会写明原因：「编号不符合登记册掩码」「值不在字典中」「行的阶段与登记册阶段不一致」。拒绝提示的文字本身就是修改指引。',
        '## 按钮不可点击',
        '把鼠标停在按钮上：提示会说明原因——「请指定区域：它计入文件编号」「没有可用合同」「路线至少需要一个步骤」。',
        '## 板块打不开',
        '「权限不足」表示该角色未被授予此板块。权限由管理员在「角色与权限」页签中调整。',
        '## 看不到其他方的数据',
        '这是设计如此：承包商只见自己的数据，员工只见其角色和所属方允许的数据。',
        '## 表格样子不一样',
        '列布局是个人设置，保存在浏览器中。「恢复列的组成、宽度和顺序」可将其还原为初始状态。',
        '## 提交问题时请附上',
        '• 文件、登记册或传递单编号；',
        '• 时间与拒绝提示的文字；',
        '• 若拒绝发生在导入时，请附上该文件。',
        '每个操作都会写入审计日志，任何问题都据此追查。',
      ],
    },
  },
]

/** Строка тела: «## » — подзаголовок, «• » — пункт списка, иначе абзац. */
function Line({ text }: { text: string }) {
  if (text.startsWith('## ')) {
    return (
      <div style={{ marginTop: 14, marginBottom: 6, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 600, lineHeight: 1.5, color: 'var(--text-strong)' }}>
        {text.slice(3)}
      </div>
    )
  }
  if (text.startsWith('• ')) {
    return (
      <div style={{ display: 'flex', gap: 8, marginBottom: 4, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', lineHeight: 1.55, color: 'var(--text-body)' }}>
        <span style={{ color: 'var(--text-subtle)' }}>•</span><span>{text.slice(2)}</span>
      </div>
    )
  }
  return (
    <p style={{ margin: '0 0 8px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', lineHeight: 1.6, color: 'var(--text-body)' }}>{text}</p>
  )
}

/* Заголовок и тело статьи на языке интерфейса — всеми тремя языками, включая
   китайский: справку читает и китайский подрядчик, а отправлять его к
   английскому тексту значит оставить без ответа того, кому она нужнее всех. */
const titleOf = (a: HelpArticle, lang: Lang) => a.title[lang] ?? a.title.en
const bodyOf = (a: HelpArticle, lang: Lang) => a.body[lang] ?? a.body.en

export function HelpPanel({ open, onClose, lang }: { open: boolean; onClose: () => void; lang: Lang }) {
  const [sel, setSel] = useState(HELP[0]!.id)
  const [q, setQ] = useState('')

  /* Поиск идёт и по телу статьи: спрашивают словом из отказа («маска», «код»)
     или названием кнопки, а не названием раздела. */
  const found = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return HELP
    return HELP.filter((a) => (titleOf(a, lang) + ' ' + bodyOf(a, lang).join(' ')).toLowerCase().includes(needle))
  }, [q, lang])

  const current = found.find((a) => a.id === sel) ?? found[0]

  return (
    <Drawer open={open} onClose={onClose} title={t(lang, 'Справка и поддержка', 'Help & support')}>
      <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
        <div style={{ width: 208, flex: '0 0 auto', borderRight: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ padding: 10, borderBottom: '1px solid var(--border-subtle)' }}>
            <Input size="sm" value={q} onChange={(e) => setQ(e.target.value)}
                   leading={<Icon name="search" size={13} />}
                   placeholder={t(lang, 'Поиск по справке…', 'Search help…')} />
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 6 }}>
            {found.map((a) => (
              <button key={a.id} type="button" className="tdms-rail" onClick={() => setSel(a.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 9px',
                        marginBottom: 1, border: 0, borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                        textAlign: 'left', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', lineHeight: 1.4,
                        background: current?.id === a.id ? 'var(--accent-soft)' : 'transparent',
                        color: current?.id === a.id ? 'var(--accent-ink)' : 'var(--text-body)',
                        fontWeight: current?.id === a.id ? 600 : 400,
                      }}>
                <Icon name={a.icon} size={14} color={current?.id === a.id ? 'var(--accent-ink)' : 'var(--text-subtle)'} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleOf(a, lang)}</span>
              </button>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: 20 }}>
          {current ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
                <Icon name={current.icon} size={18} color="var(--accent-ink)" />
                <h3 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 600, lineHeight: 1.3, color: 'var(--text-strong)' }}>{titleOf(current, lang)}</h3>
              </div>
              {bodyOf(current, lang).map((line, i) => <Line key={i} text={line} />)}
            </>
          ) : (
            <EmptyState icon="search" title={t(lang, 'Ничего не найдено', 'Nothing found')}
                        text={t(lang, 'Попробуйте другое слово — поиск идёт и по тексту статей.',
                                      'Try another word — the search covers the article texts too.')} />
          )}
        </div>
      </div>
    </Drawer>
  )
}
